import { useListState } from "@/hooks/use-list-state";
import { Fragment, useState, useEffect, useRef, useMemo, useCallback, type ReactNode } from "react";
import { useSearch, useLocation, Link } from "wouter";
import { useTabQueryParam } from "@/hooks/use-tab-query-param";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { DeviceSettings, LockMemberAssignment, LockHealthMute } from "@shared/schema";
import { DEFAULT_ACCOUNT_TIMEZONE, timezoneLabel } from "@/lib/timezones";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SearchableSelect } from "@/components/searchable-select";
import { ViewOnlyBanner, usePermissions } from "@/hooks/use-permissions";
import AppLayout from "@/components/app-layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuPortal,
} from "@/components/ui/dropdown-menu";
import {
  ArrowLeft,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Battery,
  BatteryMedium,
  BatteryLow,
  ChevronRight,
  History,
  Lock,
  LockOpen,
  MoreVertical,
  Search,
  Shield,
  Signal,
  User,
  Wrench,
  Home,
  Wifi,
  Clock,
  Key,
  Pencil,
  Ban,
  Hash,
  Plus,
  AlertCircle,
  Trash2,
  MoreHorizontal,
  RefreshCw,
  Zap,
  ExternalLink,
  BellOff,
  Bell,
  Filter,
  Check,
  WifiOff,
  Loader2,
  KeyRound,
  Copy,
} from "lucide-react";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { PadsplitSyncTab } from "@/components/smart-locks/padsplit-sync-tab";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { formatRelativeSync } from "@/lib/formatRelativeSync";
import type { Property, Room, Member } from "@shared/schema";
import { sortProperties } from "@/lib/property-utils";
import { usePageTitle } from "@/hooks/use-page-title";

type SmartLocksTab = "connected_accounts" | "padsplit_sync" | "settings";
type ConnectStep =
  | "choose_provider"
  | "ttlock_form"
  | "sifely_step1"
  | "sifely_step2"
  | "seam_picker"
  | "seam_waiting";

// --- Types used by SmartLocksPropertyBrowser ---
type ViewMode = "properties" | "locks" | "lockDetail";
export type DrawerTab = "access_codes" | "manage" | "activity";

export interface TtlockCachedLock {
  id: number;
  ttlockLockId: number;
  lockAlias: string | null;
  lockName: string | null;
  electricQuantity: number | null;
  hasGateway: boolean;
  groupId: number | null;
  groupName: string | null;
  propertyId: number | null;
  roomId: number | null;
  accessScope: "common" | "admin";
  syncedAt: string | null;
  connectionId: number;
  /** Task #1729 — host-marked as non-PadSplit; excluded from property lists and health checks */
  ignored?: boolean;
}

interface TtlockUnlockRecord {
  id: number;
  ttlockRecordId: string;
  recordType: number | null;
  recordTypeLabel: string | null;
  username: string | null;
  success: number | null;
  recordTime: string | null;
  createdAt: string;
  source: "poll" | "webhook" | null;
}

export interface TtlockConnectionInfo {
  id: number;
  userId: number;
  ttlockUsername: string;
  /** Task #3025 — 'ttlock' (default) or 'sifely'; both run on the TTLock platform. */
  provider?: "ttlock" | "sifely";
  status: string;
  expiresAt: string;
  createdAt: string;
  /** Task #2235 — count of locks with no property assignment (excludes ignored). */
  unassignedLockCount?: number;
  /** Task #3053 — Sifely webhook token, used to reconstruct the webhook URL in
   * the connection card. Only present for Sifely connections. */
  sifelyWebhookToken?: string | null;
}

interface SeamConnectionItem {
  id: number;
  provider: string;
  accountEmail: string | null;
  deviceType: string;
  status: string | null;
  /** Task #2242 — count of Seam locks with no property pin and no name-match
   * (excludes ignored). Surfaced in the Devices page "unassigned locks"
   * callout alongside TTLock's count. */
  unassignedLockCount?: number;
}

interface SeamRawAccessCode {
  access_code_id: string;
  name: string;
  code: string | null;
  status: string;
  created_at?: string;
}

interface TtlockRawPasscode {
  keyboardPwdId: number;
  keyboardPwd: string;
  keyboardPwdName: string;
  keyboardPwdType: number;
  startDate?: number;
  endDate?: number;
}

function extractErrMsg(e: unknown, fallback: string): string {
  if (e instanceof Error) return e.message;
  if (typeof e === "object" && e !== null && "message" in e) {
    const m = (e as { message?: unknown }).message;
    if (typeof m === "string") return m;
  }
  return fallback;
}

export interface UnifiedLock {
  id: string;
  name: string;
  roomId: number;
  roomNumber: string;
  propertyId: number | null;
  brand: string;
  model: string;
  status: "locked" | "unlocked";
  battery: number;
  online: boolean;
  lastActivity: string;
  autoLock: boolean | null;
  autoLockDelay: number | null;
  isReal?: boolean;
  provider?: "ttlock" | "seam" | "sifely";
  connectionId?: number;
  rawLockId?: number;
  assignedLabel?: string | null;
  accessScope?: "common" | "admin" | "room";
  hasGateway?: boolean;
  /** Task #1729 — host-marked as non-PadSplit; excluded from property lists and health checks */
  ignored?: boolean;
}

interface ProviderLock {
  deviceId: string;
  displayName: string | null;
  batteryPct: number | null;
  online: boolean;
  locked: boolean | null;
  locationName: string | null;
  propertyId: number;
  provider: "seam" | "ttlock" | "sifely";
  connectionId: number;
  connectionStatus: string;
  roomId: number | null;
  isCommonSpace: boolean;
  accessScope?: "common" | "admin";
  hasGateway: boolean;
}

type SeamPropertyLock = ProviderLock & { provider: "seam" };
type TtlockPropertyLock = ProviderLock & { provider: "ttlock" };

interface AccessCode {
  id: string;
  name: string;
  code: string;
  type: "member" | "vendor";
  status: "active" | "inactive";
  memberId?: number;
  vendorId?: number;
  createdAt: string;
  lastUsed?: string;
}

// --- Shared helper components ---

function AccessScopeBadge({ scope }: { scope?: "common" | "admin" | "room" }) {
  if (scope !== "admin" && scope !== "common" && scope !== "room") return null;
  if (scope === "admin") {
    return (
      <Badge
        variant="outline"
        className="text-[9px] px-1.5 py-0 bg-amber-50 text-amber-700 border-amber-200"
        data-testid="badge-access-scope-admin"
      >
        Admin
      </Badge>
    );
  }
  if (scope === "room") {
    return (
      <Badge
        variant="outline"
        className="text-[9px] px-1.5 py-0 bg-sky-50 text-sky-700 border-sky-200"
        data-testid="badge-access-scope-room"
      >
        Room
      </Badge>
    );
  }
  return (
    <Badge
      variant="outline"
      className="text-[9px] px-1.5 py-0 bg-gray-50 text-gray-600 border-gray-200"
      data-testid="badge-access-scope-common"
    >
      Common
    </Badge>
  );
}

function BatteryIcon({ level }: { level: number }) {
  if (level > 60) return <Battery className="h-4 w-4 text-green-500" />;
  if (level > 30) return <BatteryMedium className="h-4 w-4 text-yellow-500" />;
  return <BatteryLow className="h-4 w-4 text-red-500" />;
}

function ProviderBadge({ provider }: { provider?: "ttlock" | "seam" | "sifely" }) {
  if (!provider) return null;
  if (provider === "ttlock") {
    return (
      <Badge variant="outline" className="text-[9px] px-1.5 py-0 bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800">
        TTLock
      </Badge>
    );
  }
  if (provider === "sifely") {
    return (
      <Badge variant="outline" className="text-[9px] px-1.5 py-0 bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800">
        Sifely
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-[9px] px-1.5 py-0 bg-purple-50 text-purple-700 border-purple-200">
      Other
    </Badge>
  );
}

function PropertyCard({
  property,
  roomCount,
  lockCount,
  onClick,
}: {
  property: Property;
  roomCount: number;
  lockCount: number;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="bg-white border border-gray-200 rounded-xl p-5 text-left hover:border-brand/30 hover:shadow-md transition-all group"
      data-testid={`card-property-${property.id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-brand/10 flex items-center justify-center">
            <Home className="h-5 w-5 text-brand" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 text-sm group-hover:text-brand transition-colors">
              {property.name}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">{property.address || "No address"}</p>
          </div>
        </div>
        <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-brand transition-colors" />
      </div>
      <div className="flex items-center gap-4 mt-4">
        <div className="flex items-center gap-1.5">
          <Lock className="h-3.5 w-3.5 text-gray-400" />
          <span className="text-xs text-gray-600">{lockCount} Locks</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Key className="h-3.5 w-3.5 text-gray-400" />
          <span className="text-xs text-gray-600">{roomCount} Rooms</span>
        </div>
        <Badge
          variant="outline"
          className={`ml-auto text-[10px] ${
            property.status === "active"
              ? "bg-green-50 text-green-700 border-green-200"
              : "bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800"
          }`}
        >
          {property.status === "active" ? "Active" : "Launch"}
        </Badge>
      </div>
    </button>
  );
}

function LockCard({
  lock,
  onClick,
  cardRef,
  flashing,
}: {
  lock: UnifiedLock;
  onClick: () => void;
  cardRef?: (el: HTMLButtonElement | null) => void;
  flashing?: boolean;
}) {
  return (
    <button
      ref={cardRef}
      onClick={onClick}
      className={`bg-white border rounded-xl p-4 text-left hover:border-brand/30 hover:shadow-md transition-all duration-700 group w-full ${
        flashing
          ? "border-yellow-400 bg-yellow-50 ring-2 ring-inset ring-yellow-400"
          : "border-gray-200"
      }`}
      data-testid={`card-lock-${lock.id}`}
      data-highlighted={flashing ? "true" : undefined}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              lock.status === "locked" ? "bg-green-50" : "bg-yellow-50"
            }`}
          >
            {lock.status === "locked" ? (
              <Lock className="h-5 w-5 text-green-600" />
            ) : (
              <LockOpen className="h-5 w-5 text-yellow-600" />
            )}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h4 className="font-medium text-gray-900 text-sm">{lock.name}</h4>
              <ProviderBadge provider={lock.provider} />
              <AccessScopeBadge scope={lock.accessScope} />
            </div>
            <p className="text-xs text-gray-500">
              {lock.brand} {lock.model}
            </p>
            {lock.assignedLabel && (
              <p
                className="text-[11px] text-brand mt-0.5"
                data-testid={`text-lock-assignment-${lock.id}`}
              >
                {lock.assignedLabel}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <BatteryIcon level={lock.battery} />
            <span className="text-xs text-gray-500">{lock.battery}%</span>
          </div>
          <div className="flex items-center gap-1">
            <Signal
              className={`h-3.5 w-3.5 ${lock.online ? "text-green-500" : "text-gray-300"}`}
            />
            <span className="text-[10px] text-gray-400">
              {lock.online ? "Online" : "Offline"}
            </span>
          </div>
          <Badge
            variant="outline"
            className={`text-[10px] ${
              lock.status === "locked"
                ? "bg-green-50 text-green-700 border-green-200"
                : "bg-yellow-50 text-yellow-700 border-yellow-200"
            }`}
          >
            {lock.status === "locked" ? "Locked" : "Unlocked"}
          </Badge>
          <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-brand transition-colors" />
        </div>
      </div>
      {lock.lastActivity && (
        <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-4">
          <div className="flex items-center gap-1.5">
            <Clock className="h-3 w-3 text-gray-400" />
            <span className="text-[11px] text-gray-500">Last activity: {lock.lastActivity}</span>
          </div>
          {lock.autoLock && (
            <div className="flex items-center gap-1.5">
              <Shield className="h-3 w-3 text-brand" />
              <span className="text-[11px] text-brand">Auto-lock on</span>
            </div>
          )}
        </div>
      )}
    </button>
  );
}

function AccessCodeCard({
  code,
  onEdit,
  onRevoke,
}: {
  code: AccessCode;
  onEdit: () => void;
  onRevoke: () => void;
}) {
  return (
    <div
      className="bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between"
      data-testid={`card-access-code-${code.id}`}
    >
      <div className="flex items-center gap-3">
        <div
          className={`w-9 h-9 rounded-full flex items-center justify-center ${
            code.type === "member" ? "bg-brand/10" : "bg-orange-50"
          }`}
        >
          {code.type === "member" ? (
            <User className="h-4 w-4 text-brand" />
          ) : (
            <Wrench className="h-4 w-4 text-orange-600" />
          )}
        </div>
        <div>
          <p className="text-sm font-medium text-gray-900">{code.name}</p>
          <div className="flex items-center gap-2 mt-0.5">
            <div className="flex items-center gap-1">
              <Hash className="h-3 w-3 text-gray-400" />
              <span className="text-xs font-mono text-gray-500">{code.code}</span>
            </div>
            {code.lastUsed && (
              <span className="text-[10px] text-gray-400">· Last used {code.lastUsed}</span>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Badge
          variant="outline"
          className={`text-[10px] ${
            code.status === "active"
              ? "bg-green-50 text-green-700 border-green-200"
              : "bg-gray-50 text-gray-500 border-gray-200"
          }`}
        >
          {code.status === "active" ? "Active" : "Inactive"}
        </Badge>
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <button
              className="p-1 hover:bg-gray-100 rounded"
              data-testid={`button-code-menu-${code.id}`}
            >
              <MoreVertical className="h-4 w-4 text-gray-400" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuPortal>
            <DropdownMenuContent align="end" className="z-[9999]">
              <DropdownMenuItem onClick={onEdit} data-testid={`button-edit-code-${code.id}`}>
                <Pencil className="h-4 w-4 mr-2" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={onRevoke}
                className="text-red-600"
                data-testid={`button-revoke-code-${code.id}`}
              >
                <Ban className="h-4 w-4 mr-2" />
                Revoke
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenuPortal>
        </DropdownMenu>
      </div>
    </div>
  );
}

function TtlockLockRow({
  lock,
  activeProperties,
  readOnly,
  groupPropertyId,
  onAssign,
  onActivityOpen,
  onIgnore,
}: {
  lock: TtlockCachedLock;
  activeProperties: Property[];
  readOnly: boolean;
  groupPropertyId?: string | null;
  onAssign: (ttlockLockId: number, propertyId: number | null, roomId: number | null, accessScope: "common" | "admin") => void;
  onActivityOpen: () => void;
  onIgnore?: (ttlockLockId: number, ignored: boolean) => void;
}) {
  // Task #1729 — Derive the initial room selection correctly:
  // - "admin" when the lock uses admin-only access
  // - the room ID when a specific room is assigned
  // - "common" when the lock is pinned to a property but no specific room (common space)
  // - "none" only when the lock is fully unassigned (no property, no room)
  const deriveLocalRoomId = (
    accessScope: "common" | "admin",
    roomId: number | null | undefined,
    propertyId: number | null | undefined,
  ) =>
    accessScope === "admin"
      ? "admin"
      : roomId != null
        ? String(roomId)
        : propertyId != null
          ? "common"
          : "none";

  const [localRoomId, setLocalRoomId] = useState<string>(
    deriveLocalRoomId(lock.accessScope, lock.roomId, lock.propertyId)
  );

  useEffect(() => {
    setLocalRoomId(deriveLocalRoomId(lock.accessScope, lock.roomId, lock.propertyId));
  }, [lock.roomId, lock.accessScope, lock.propertyId]);

  const effectivePropertyId = lock.propertyId != null ? String(lock.propertyId) : "none";
  const propertyIdNum = effectivePropertyId !== "none" ? parseInt(effectivePropertyId, 10) : null;
  void groupPropertyId;

  const roomsQuery = useQuery<Room[]>({
    queryKey: ["/api/app/properties", propertyIdNum, "rooms"],
    queryFn: async () => {
      if (!propertyIdNum) return [];
      const res = await fetch(`/api/app/properties/${propertyIdNum}/rooms`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!propertyIdNum,
    staleTime: 60_000,
  });

  const filteredRooms = (roomsQuery.data || []).filter(
    (r) => r.roomType !== "bathroom" && !r.roomNumber?.toLowerCase().startsWith("bath")
  );

  const handleRoomChange = (val: string) => {
    setLocalRoomId(val);
    const propId = effectivePropertyId === "none" ? null : parseInt(effectivePropertyId, 10);
    if (val === "admin") {
      onAssign(lock.ttlockLockId, propId, null, "admin");
    } else if (val === "none") {
      // Task #1729 — "None" means fully unassigned (clears propertyId + roomId).
      onAssign(lock.ttlockLockId, null, null, "common");
    } else if (val === "common") {
      onAssign(lock.ttlockLockId, propId, null, "common");
    } else {
      const roomId = parseInt(val, 10);
      onAssign(lock.ttlockLockId, propId, roomId, "common");
    }
  };

  const handlePropertyChange = (val: string) => {
    const newPropId = val === "none" ? null : parseInt(val, 10);
    // Clear the room to "none" (safe default — no auto common-space).
    setLocalRoomId("none");
    // Task #1729 — when clearing the property, fully unassign the lock.
    onAssign(lock.ttlockLockId, newPropId, null, "common");
  };

  const lockName = lock.lockAlias || lock.lockName || `Lock ${lock.ttlockLockId}`;
  const battery = lock.electricQuantity;

  const propertySelectLabel =
    effectivePropertyId === "none"
      ? "Unassigned"
      : activeProperties.find((p) => String(p.id) === effectivePropertyId)?.name;

  const roomSelectValue = effectivePropertyId === "none" ? "none" : localRoomId;
  const roomSelectLabel =
    roomSelectValue === "none"
      ? "None (not a PadSplit room)"
      : roomSelectValue === "common"
        ? "Common space"
        : roomSelectValue === "admin"
          ? "Admin"
          : (() => {
              const r = filteredRooms.find((room) => String(room.id) === roomSelectValue);
              return r ? r.roomNumber || (r as any).name || `Room ${r.id}` : undefined;
            })();
  const optimisticScope: "common" | "admin" | "room" =
    localRoomId === "admin"
      ? "admin"
      : localRoomId !== "common" && localRoomId !== "none" && !isNaN(parseInt(localRoomId, 10))
        ? "room"
        : "common";

  // Task #1729 — ignored rows are dimmed; controls disabled.
  const isIgnored = lock.ignored === true;

  return (
    <div
      className={`bg-white border rounded-lg px-3 py-2.5 flex items-center gap-3 flex-wrap ${isIgnored ? "border-gray-100 opacity-60" : "border-gray-200"}`}
      data-testid={`row-cached-lock-${lock.ttlockLockId}`}
    >
      <Lock className="h-4 w-4 text-gray-400 flex-shrink-0" />
      <div className="flex-1 min-w-0 flex items-center gap-2">
        <p className="text-sm font-medium text-gray-900 truncate">{lockName}</p>
        {isIgnored ? (
          <Badge variant="outline" className="text-[9px] px-1.5 py-0 bg-gray-50 text-gray-500 border-gray-200">
            Ignored
          </Badge>
        ) : (
          <AccessScopeBadge scope={optimisticScope} />
        )}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
        {!isIgnored && battery !== null && battery !== undefined && (
          <div className="flex items-center gap-1">
            {battery < 20 ? (
              <BatteryLow className="h-4 w-4 text-red-500" />
            ) : battery < 50 ? (
              <BatteryMedium className="h-4 w-4 text-yellow-500" />
            ) : (
              <Battery className="h-4 w-4 text-green-500" />
            )}
            <span className="text-xs text-gray-500">{battery}%</span>
          </div>
        )}
        {!isIgnored && lock.hasGateway && (
          <Badge variant="outline" className="text-[9px] px-1.5 py-0 bg-green-50 text-green-700 border-green-200">
            Gateway
          </Badge>
        )}
        {!isIgnored && !lock.hasGateway && lock.propertyId != null && (
          <Badge variant="outline" className="text-[9px] px-1.5 py-0 bg-red-50 text-red-700 border-red-200">
            No Gateway
          </Badge>
        )}
        {!isIgnored && (
          <>
            <SearchableSelect
              value={effectivePropertyId}
              onValueChange={handlePropertyChange}
              disabled={readOnly}
              placeholder="Property"
              searchPlaceholder="Search properties..."
              emptyText="No properties match."
              testId={`select-property-${lock.ttlockLockId}`}
              className="h-6 text-xs w-72 border-gray-200"
              triggerProps={{ title: propertySelectLabel || undefined }}
              options={[
                { value: "none", label: "Unassigned" },
                ...activeProperties.map((p) => ({
                  value: String(p.id),
                  label: p.name,
                  keywords: p.address ? `${p.name} ${p.address}` : undefined,
                })),
              ]}
            />
            <Select
              value={roomSelectValue}
              onValueChange={handleRoomChange}
              disabled={readOnly || effectivePropertyId === "none"}
            >
              <SelectTrigger
                className="h-6 text-xs w-72 border-gray-200"
                data-testid={`select-room-${lock.ttlockLockId}`}
                title={roomSelectLabel || undefined}
              >
                <SelectValue placeholder="Room or area" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None (not a PadSplit room)</SelectItem>
                <SelectItem value="common">Common space</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                {filteredRooms.map((r) => (
                  <SelectItem key={r.id} value={String(r.id)}>
                    {r.roomNumber || (r as any).name || `Room ${r.id}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 px-2 text-xs text-gray-500 hover:text-gray-900"
              onClick={onActivityOpen}
              data-testid={`button-activity-${lock.ttlockLockId}`}
            >
              <Clock className="h-3.5 w-3.5 mr-1" />
              Activity
            </Button>
          </>
        )}
        {onIgnore && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="p-1 hover:bg-gray-100 rounded"
                data-testid={`button-lock-actions-${lock.ttlockLockId}`}
              >
                <MoreHorizontal className="h-3.5 w-3.5 text-gray-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuPortal>
              <DropdownMenuContent align="end">
                {!isIgnored && !readOnly && (
                  <DropdownMenuItem
                    onSelect={() => onIgnore(lock.ttlockLockId, true)}
                    data-testid={`button-ignore-lock-${lock.ttlockLockId}`}
                  >
                    <Ban className="h-4 w-4 mr-2 text-gray-500" />
                    Ignore (not a PadSplit lock)
                  </DropdownMenuItem>
                )}
                {isIgnored && !readOnly && (
                  <DropdownMenuItem
                    onSelect={() => onIgnore(lock.ttlockLockId, false)}
                    data-testid={`button-unignore-lock-${lock.ttlockLockId}`}
                  >
                    <Check className="h-4 w-4 mr-2 text-gray-500" />
                    Restore (use for PadSplit)
                  </DropdownMenuItem>
                )}
                {isIgnored && (
                  <DropdownMenuItem
                    disabled
                    className="text-xs text-gray-400 cursor-default"
                  >
                    Ignored — excluded from all properties
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenuPortal>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}

export function TtlockConnectionCard({
  conn,
  readOnly,
  activeProperties,
  onReconnect,
  onDisconnect,
  onActivityOpen,
  forceExpandSignal,
  cardRef,
}: {
  conn: TtlockConnectionInfo;
  readOnly: boolean;
  activeProperties: Property[];
  onReconnect: (conn: TtlockConnectionInfo) => void;
  onDisconnect: (id: number) => void;
  onActivityOpen: (args: { connectionId: number; ttlockLockId: number; name: string }) => void;
  /** Task #2235 — incrementing signal that forces this card to expand (e.g. when the
   * "unassigned locks" callout is clicked). */
  forceExpandSignal?: number;
  cardRef?: (el: HTMLDivElement | null) => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [expanded, setExpanded] = useState(false);

  // Task #2235 — when the parent bumps forceExpandSignal, open this card.
  useEffect(() => {
    if (forceExpandSignal && forceExpandSignal > 0) {
      setExpanded(true);
    }
  }, [forceExpandSignal]);
  const [connScopeFilter, setConnScopeFilter] = useState<Set<"common" | "room" | "admin">>(new Set());
  const [connGatewayDisconnectedOnly, setConnGatewayDisconnectedOnly] = useState(false);
  const [connUnassignedOnly, setConnUnassignedOnly] = useState(false);
  const [connLowBatteryOnly, setConnLowBatteryOnly] = useState(false);

  const locksQuery = useQuery<TtlockCachedLock[]>({
    queryKey: ["/api/app/devices/ttlock/connections", conn.id, "locks"],
    queryFn: async () => {
      const res = await fetch(`/api/app/devices/ttlock/connections/${conn.id}/locks`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: expanded,
  });

  const syncMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async () => {
      const res = await fetch(`/api/app/devices/ttlock/connections/${conn.id}/sync`, {
        method: "POST",
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Sync failed");
      return data;
    },
    onSuccess: (data: { lockCount: number }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/ttlock/connections", conn.id, "locks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/ttlock/connections"] });
      toast({ title: `Found ${data.lockCount} lock${data.lockCount === 1 ? "" : "s"}` });
    },
    onError: (err: unknown) => {
      toast({ title: extractErrMsg(err, "Sync failed"), variant: "destructive" });
    },
  });

  const assignMutation = useMutation({
    mutationFn: async ({ ttlockLockId, propertyId, roomId, accessScope }: { ttlockLockId: number; propertyId: number | null; roomId: number | null; accessScope?: "common" | "admin" }) => {
      const res = await fetch(`/api/app/devices/ttlock/locks/${ttlockLockId}/assign-room`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ connectionId: conn.id, propertyId, roomId, accessScope: accessScope ?? "common" }),
      });
      if (!res.ok) throw new Error("Failed to save assignment");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/ttlock/connections", conn.id, "locks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/locks/list-for-property"] });
      // Task #2235 — refresh the connection list so the unassigned-locks callout
      // count on the Devices page updates in real time as locks are assigned.
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/ttlock/connections"] });
    },
    onError: (err: unknown) => {
      toast({ title: extractErrMsg(err, "Failed to save assignment"), variant: "destructive" });
    },
  });

  // Task #1729 — ignore/unignore a TTLock lock.
  const ignoreMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async ({ ttlockLockId, ignored }: { ttlockLockId: number; ignored: boolean }) => {
      const res = await fetch(`/api/app/devices/ttlock/locks/${ttlockLockId}/assign-room`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ connectionId: conn.id, ignored }),
      });
      if (!res.ok) throw new Error(`Failed to ${ignored ? "ignore" : "restore"} lock`);
      return res.json();
    },
    onSuccess: (_data, { ignored }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/ttlock/connections", conn.id, "locks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/locks/list-for-property"] });
      // Task #2235 — refresh the connection list so the unassigned-locks callout
      // count reflects the ignore/restore change immediately.
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/ttlock/connections"] });
      toast({ title: ignored ? "Lock ignored — excluded from all properties" : "Lock restored" });
    },
    onError: (err: unknown) => {
      toast({ title: extractErrMsg(err, "Failed to update lock"), variant: "destructive" });
    },
  });

  const isExpired = conn.status === "token_expired" || conn.status === "revoked" || conn.status === "error";
  const cachedLocks = locksQuery.data || [];

  const sortedLocks = useMemo(() => {
    return [...cachedLocks].sort((a, b) => {
      const nameA = a.lockAlias || a.lockName || `Lock ${a.ttlockLockId}`;
      const nameB = b.lockAlias || b.lockName || `Lock ${b.ttlockLockId}`;
      return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: "base" });
    });
  }, [cachedLocks]);

  // Task #1729 — separate ignored locks so they're rendered in their own section
  // and excluded from the active filter/group logic.
  const activeSortedLocks = useMemo(() => sortedLocks.filter((l) => !l.ignored), [sortedLocks]);
  const ignoredSortedLocks = useMemo(() => sortedLocks.filter((l) => l.ignored), [sortedLocks]);
  const [showIgnored, setShowIgnored] = useState(false);
  const [bulkUnignoreSaving, setBulkUnignoreSaving] = useState(false);

  const handleBulkUnignore = async () => {
    if (bulkUnignoreSaving || ignoredSortedLocks.length === 0) return;
    setBulkUnignoreSaving(true);
    try {
      // Task #4544 — allSettled (not all): a mid-batch network failure must not
      // reject the whole batch and leak an unhandled rejection. Every lock's
      // PATCH is still attempted; each failure surfaces its own toast via
      // ignoreMutation.onError, so the host gets visual feedback per stuck lock.
      await Promise.allSettled(
        ignoredSortedLocks.map((lock) =>
          ignoreMutation.mutateAsync({ ttlockLockId: lock.ttlockLockId, ignored: false }),
        ),
      );
    } finally {
      setBulkUnignoreSaving(false);
    }
  };

  const hasActiveConnFilters = connScopeFilter.size > 0 || connGatewayDisconnectedOnly || connUnassignedOnly || connLowBatteryOnly;

  const lockEffectiveScope = (l: TtlockCachedLock): "common" | "room" | "admin" => {
    if (l.accessScope === "admin") return "admin";
    if (l.roomId != null) return "room";
    return "common";
  };

  const filteredSortedLocks = useMemo(() => {
    // Task #1729 — filter only active (non-ignored) locks; ignored locks
    // are rendered separately in their own collapsible section.
    return activeSortedLocks.filter((l) => {
      if (connScopeFilter.size > 0 && !connScopeFilter.has(lockEffectiveScope(l))) return false;
      if (connGatewayDisconnectedOnly && l.hasGateway !== false) return false;
      if (connUnassignedOnly && l.propertyId != null) return false;
      if (connLowBatteryOnly && !(l.electricQuantity != null && l.electricQuantity < 20)) return false;
      return true;
    });
  }, [activeSortedLocks, connScopeFilter, connGatewayDisconnectedOnly, connUnassignedOnly, connLowBatteryOnly]);

  const groupedLocks = useMemo(() => {
    return filteredSortedLocks.reduce<Record<string, TtlockCachedLock[]>>((acc, lock) => {
      const g = lock.groupName || "Ungrouped";
      if (!acc[g]) acc[g] = [];
      acc[g].push(lock);
      return acc;
    }, {});
  }, [filteredSortedLocks]);

  // Unfiltered grouping — used for group-level property reassignment
  // so that hiding rows via filters never silently narrows which locks
  // a "set property for this group" action applies to.
  // Task #1729 — also uses only active (non-ignored) locks.
  const groupedLocksAll = useMemo(() => {
    return activeSortedLocks.reduce<Record<string, TtlockCachedLock[]>>((acc, lock) => {
      const g = lock.groupName || "Ungrouped";
      if (!acc[g]) acc[g] = [];
      acc[g].push(lock);
      return acc;
    }, {});
  }, [activeSortedLocks]);

  const clearConnFilters = () => {
    setConnScopeFilter(new Set());
    setConnGatewayDisconnectedOnly(false);
    setConnUnassignedOnly(false);
    setConnLowBatteryOnly(false);
  };

  // Task #2416 — quick-filter chip counts (over active/non-ignored locks).
  const chipCounts = useMemo(() => {
    let unassigned = 0;
    let gatewayDisconnected = 0;
    let lowBattery = 0;
    let admin = 0;
    let common = 0;
    let room = 0;
    for (const l of activeSortedLocks) {
      if (l.propertyId == null) unassigned++;
      if (l.hasGateway === false) gatewayDisconnected++;
      // Task #2417 — match the lock row low-battery badge threshold (< 20%).
      if (l.electricQuantity != null && l.electricQuantity < 20) lowBattery++;
      const scope = lockEffectiveScope(l);
      if (scope === "admin") admin++;
      else if (scope === "common") common++;
      else room++;
    }
    return { unassigned, gatewayDisconnected, lowBattery, admin, common, room };
  }, [activeSortedLocks]);

  const toggleScopeFilter = (key: "common" | "room" | "admin") => {
    setConnScopeFilter((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const sortedGroupNames = useMemo(() => {
    return Object.keys(groupedLocks).sort((a, b) => {
      if (a === "Ungrouped") return 1;
      if (b === "Ungrouped") return -1;
      return a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });
    });
  }, [groupedLocks]);

  const [groupPropertyIds, setGroupPropertyIds] = useState<Record<string, string>>({});
  const [groupSaving, setGroupSaving] = useState<Record<string, boolean>>({});

  useEffect(() => {
    // Task #1729 — group property IDs only consider active (non-ignored) locks.
    if (!activeSortedLocks.length) return;
    const grouped = activeSortedLocks.reduce<Record<string, TtlockCachedLock[]>>((acc, lock) => {
      const g = lock.groupName || "Ungrouped";
      if (!acc[g]) acc[g] = [];
      acc[g].push(lock);
      return acc;
    }, {});
    const newIds: Record<string, string> = {};
    for (const [group, locks] of Object.entries(grouped)) {
      const propIds = locks.map((l) => l.propertyId);
      const allSame = propIds.every((p) => p === propIds[0]);
      newIds[group] = allSame && propIds[0] != null ? String(propIds[0]) : "none";
    }
    setGroupPropertyIds(newIds);
  }, [activeSortedLocks]);

  const handleGroupPropertyChange = async (group: string, val: string) => {
    if (val === "ignore-all") {
      const locks = groupedLocksAll[group] || [];
      setGroupSaving((prev) => ({ ...prev, [group]: true }));
      try {
        // Task #4544 — allSettled (not all): a mid-batch network failure must
        // not reject the whole batch and leak an unhandled rejection. Every
        // lock's PATCH is still attempted; each failure surfaces its own toast
        // via ignoreMutation.onError so the host gets per-lock feedback.
        await Promise.allSettled(
          locks.map((lock) =>
            ignoreMutation.mutateAsync({ ttlockLockId: lock.ttlockLockId, ignored: true }),
          ),
        );
      } finally {
        setGroupSaving((prev) => ({ ...prev, [group]: false }));
        setGroupPropertyIds((prev) => ({ ...prev, [group]: "none" }));
      }
      return;
    }
    setGroupPropertyIds((prev) => ({ ...prev, [group]: val }));
    const newPropId = val === "none" ? null : parseInt(val, 10);
    if (newPropId != null) {
      queryClient.prefetchQuery({
        queryKey: ["/api/app/properties", newPropId, "rooms"],
        queryFn: async () => {
          const res = await fetch(`/api/app/properties/${newPropId}/rooms`, { credentials: "include" });
          if (!res.ok) return [];
          return res.json();
        },
        staleTime: 60_000,
      });
    }
    const locks = groupedLocksAll[group] || [];
    setGroupSaving((prev) => ({ ...prev, [group]: true }));
    try {
      await Promise.all(
        locks.map((lock) =>
          assignMutation.mutateAsync({
            ttlockLockId: lock.ttlockLockId,
            propertyId: newPropId,
            roomId: newPropId != null && lock.propertyId === newPropId ? lock.roomId : null,
            accessScope: lock.accessScope ?? "common",
          }),
        ),
      );
    } finally {
      setGroupSaving((prev) => ({ ...prev, [group]: false }));
    }
  };

  const lastSynced = cachedLocks.length > 0
    ? cachedLocks.reduce((best, l) => l.syncedAt && (!best || new Date(l.syncedAt) > new Date(best)) ? l.syncedAt : best, null as string | null)
    : null;

  const [, setLocksNowTick] = useState(0);
  useEffect(() => {
    if (!lastSynced) return;
    const id = setInterval(() => setLocksNowTick((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, [lastSynced]);
  const lastSyncedLabel = formatRelativeSync(lastSynced);

  return (
    <div
      ref={cardRef}
      className="border border-gray-200 rounded-xl overflow-hidden" data-tour="smart-locks-list"
      data-testid={`card-ttlock-connection-${conn.id}`}
    >
      {isExpired && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2.5 flex items-center gap-2">
          <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0" />
          <p className="text-sm text-amber-800 flex-1">{conn.provider === "sifely" ? "Sifely" : "TTLock"} connection needs renewal — Reconnect to restore access.</p>
          <Button
            size="sm"
            variant="outline"
            className="border-amber-300 text-amber-700 hover:bg-amber-100 text-xs h-7 px-2"
            onClick={() => onReconnect(conn)}
            data-testid={`button-reconnect-ttlock-${conn.id}`}
          >
            <RefreshCw className="h-3 w-3 mr-1" />
            Reconnect
          </Button>
        </div>
      )}

      <div className="px-4 py-3 bg-white flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
            <Zap className="h-4 w-4 text-blue-600" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium text-gray-900">{conn.ttlockUsername}</p>
              <ProviderBadge provider={conn.provider === "sifely" ? "sifely" : "ttlock"} />
            </div>
            <div className="flex items-center gap-3 mt-0.5 flex-wrap">
              <span className={`text-xs ${isExpired ? "text-amber-600" : "text-green-600"}`}>
                {isExpired ? "Needs reconnect" : "Connected"}
              </span>
              {cachedLocks.length > 0 && (
                <span className="text-xs text-gray-400">
                  {activeSortedLocks.length} lock{activeSortedLocks.length !== 1 ? "s" : ""}
                  {ignoredSortedLocks.length > 0 && (
                    <span className="ml-1 text-gray-400">· {ignoredSortedLocks.length} ignored</span>
                  )}
                </span>
              )}
              {lastSyncedLabel && (
                <span
                  className="text-xs text-gray-400"
                  title={lastSynced ? new Date(lastSynced).toLocaleString() : undefined}
                  data-testid={`text-cached-locks-last-synced-${conn.id}`}
                >
                  Last updated {lastSyncedLabel}
                </span>
              )}
              {conn.expiresAt && !isExpired && (
                <span className="text-xs text-gray-400">Token expires {new Date(conn.expiresAt).toLocaleDateString()}</span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            size="sm"
            variant="outline"
            className="text-xs h-7 px-2"
            disabled={readOnly || isExpired || syncMutation.isPending}
            onClick={() => { setExpanded(true); syncMutation.mutate(); }}
            data-testid={`button-sync-ttlock-${conn.id}`}
          >
            <RefreshCw className={`h-3 w-3 mr-1 ${syncMutation.isPending ? "animate-spin" : ""}`} />
            Sync now
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="text-xs h-7 px-2.5"
            onClick={() => setExpanded(!expanded)}
            data-testid={`button-expand-ttlock-${conn.id}`}
          >
            <ChevronRight className={`h-3.5 w-3.5 mr-1 transition-transform ${expanded ? "rotate-90" : ""}`} />
            {expanded ? "Hide" : "See all"}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="p-1 hover:bg-gray-100 rounded" data-testid={`button-ttlock-actions-${conn.id}`}>
                <MoreHorizontal className="h-4 w-4 text-gray-500" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                disabled={readOnly}
                className="text-red-600"
                onClick={() => onDisconnect(conn.id)}
                data-testid={`button-ttlock-disconnect-${conn.id}`}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Disconnect
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-gray-100 bg-gray-50 px-4 py-3 space-y-3">
          {/* Task #3053 — let hosts re-grab their Sifely webhook URL without
              re-running the onboarding wizard. */}
          {conn.provider === "sifely" && conn.sifelyWebhookToken && (
            <div className="rounded-lg border border-gray-200 bg-white p-3">
              <p className="text-[11px] text-gray-500 mb-1 font-medium uppercase tracking-wider">Webhook URL</p>
              <div className="flex items-center gap-2">
                <code
                  className="text-xs text-gray-800 break-all flex-1"
                  data-testid={`text-sifely-webhook-url-${conn.id}`}
                >
                  {`${window.location.origin}/api/webhooks/sifely/records?token=${conn.sifelyWebhookToken}`}
                </code>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 px-2 flex-shrink-0"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(
                        `${window.location.origin}/api/webhooks/sifely/records?token=${conn.sifelyWebhookToken}`,
                      );
                      toast({ title: "Webhook URL copied" });
                    } catch {
                      toast({ title: "Couldn't copy — please copy manually", variant: "destructive" });
                    }
                  }}
                  data-testid={`button-copy-sifely-webhook-url-${conn.id}`}
                >
                  <Copy className="h-3.5 w-3.5" />
                </Button>
              </div>
              <p className="text-[11px] text-gray-500 mt-1.5">
                Paste this into your Sifely developer app's Webhook URL field.
              </p>
            </div>
          )}
          {locksQuery.isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white border border-gray-200 rounded-lg p-3 animate-pulse h-12" />
              ))}
            </div>
          ) : cachedLocks.length === 0 ? (
            <div className="text-center py-6">
              <Lock className="h-8 w-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-400">No locks in cache yet. Click "Sync now" to load locks.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8"
                      data-testid={`button-ttlock-filter-${conn.id}`}
                    >
                      <Filter className="h-3.5 w-3.5 mr-1.5" />
                      Filter
                      {hasActiveConnFilters && (
                        <Badge
                          variant="secondary"
                          className="ml-1.5 h-4 min-w-[18px] px-1 text-[10px] bg-brand text-white"
                          data-testid={`badge-ttlock-filter-count-${conn.id}`}
                        >
                          {connScopeFilter.size + (connGatewayDisconnectedOnly ? 1 : 0) + (connUnassignedOnly ? 1 : 0) + (connLowBatteryOnly ? 1 : 0)}
                        </Badge>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56">
                    <div className="px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                      Lock type
                    </div>
                    {([
                      { key: "admin", label: "Admin" },
                      { key: "common", label: "Common" },
                      { key: "room", label: "Room" },
                    ] as const).map((opt) => {
                      const checked = connScopeFilter.has(opt.key);
                      return (
                        <DropdownMenuItem
                          key={opt.key}
                          onSelect={(e) => {
                            e.preventDefault();
                            setConnScopeFilter((prev) => {
                              const next = new Set(prev);
                              if (next.has(opt.key)) next.delete(opt.key); else next.add(opt.key);
                              return next;
                            });
                          }}
                          data-testid={`filter-ttlock-scope-${conn.id}-${opt.key}`}
                          className="flex items-center justify-between cursor-pointer"
                        >
                          <span>{opt.label}</span>
                          {checked && <Check className="h-3.5 w-3.5 text-brand" />}
                        </DropdownMenuItem>
                      );
                    })}
                    <div className="px-2 pt-2 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500 border-t border-gray-100 mt-1">
                      Gateway
                    </div>
                    <DropdownMenuItem
                      onSelect={(e) => { e.preventDefault(); setConnGatewayDisconnectedOnly((v) => !v); }}
                      data-testid={`filter-ttlock-gateway-disconnected-${conn.id}`}
                      className="flex items-center justify-between cursor-pointer"
                    >
                      <span className="flex items-center gap-1.5">
                        <WifiOff className="h-3.5 w-3.5 text-gray-500" />
                        Gateway disconnected
                      </span>
                      {connGatewayDisconnectedOnly && <Check className="h-3.5 w-3.5 text-brand" />}
                    </DropdownMenuItem>
                    <div className="px-2 pt-2 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500 border-t border-gray-100 mt-1">
                      Battery
                    </div>
                    <DropdownMenuItem
                      onSelect={(e) => { e.preventDefault(); setConnLowBatteryOnly((v) => !v); }}
                      data-testid={`filter-ttlock-low-battery-${conn.id}`}
                      className="flex items-center justify-between cursor-pointer"
                    >
                      <span className="flex items-center gap-1.5">
                        <BatteryLow className="h-3.5 w-3.5 text-red-500" />
                        Low battery
                      </span>
                      {connLowBatteryOnly && <Check className="h-3.5 w-3.5 text-brand" />}
                    </DropdownMenuItem>
                    <div className="px-2 pt-2 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500 border-t border-gray-100 mt-1">
                      Assignment
                    </div>
                    <DropdownMenuItem
                      onSelect={(e) => { e.preventDefault(); setConnUnassignedOnly((v) => !v); }}
                      data-testid={`filter-ttlock-unassigned-${conn.id}`}
                      className="flex items-center justify-between cursor-pointer"
                    >
                      <span>Unassigned</span>
                      {connUnassignedOnly && <Check className="h-3.5 w-3.5 text-brand" />}
                    </DropdownMenuItem>
                    {hasActiveConnFilters && (
                      <DropdownMenuItem
                        onSelect={(e) => { e.preventDefault(); clearConnFilters(); }}
                        data-testid={`filter-ttlock-clear-${conn.id}`}
                        className="cursor-pointer text-gray-600 border-t border-gray-100 mt-1"
                      >
                        Clear all filters
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
                {hasActiveConnFilters && (
                  <span className="text-xs text-gray-500" data-testid={`text-ttlock-filter-count-${conn.id}`}>
                    Showing {filteredSortedLocks.length} of {activeSortedLocks.length} lock{activeSortedLocks.length !== 1 ? "s" : ""}
                  </span>
                )}
              </div>
              {/* Task #2416 — quick-filter chips for at-a-glance counts and one-click filtering. */}
              {(() => {
                type Chip = {
                  key: string;
                  label: string;
                  count: number;
                  active: boolean;
                  onClick: () => void;
                  icon?: JSX.Element;
                };
                const chips: Chip[] = [];
                if (chipCounts.unassigned > 0) {
                  chips.push({
                    key: "unassigned",
                    label: "unassigned",
                    count: chipCounts.unassigned,
                    active: connUnassignedOnly,
                    onClick: () => setConnUnassignedOnly((v) => !v),
                  });
                }
                if (chipCounts.gatewayDisconnected > 0) {
                  chips.push({
                    key: "gateway-disconnected",
                    label: "gateway disconnected",
                    count: chipCounts.gatewayDisconnected,
                    active: connGatewayDisconnectedOnly,
                    onClick: () => setConnGatewayDisconnectedOnly((v) => !v),
                    icon: <WifiOff className="h-3 w-3" />,
                  });
                }
                if (chipCounts.lowBattery > 0) {
                  chips.push({
                    key: "low-battery",
                    label: "low battery",
                    count: chipCounts.lowBattery,
                    active: connLowBatteryOnly,
                    onClick: () => setConnLowBatteryOnly((v) => !v),
                    icon: <BatteryLow className="h-3 w-3" />,
                  });
                }
                ([
                  { key: "admin" as const, label: "Admin", count: chipCounts.admin },
                  { key: "common" as const, label: "Common", count: chipCounts.common },
                  { key: "room" as const, label: "Room", count: chipCounts.room },
                ]).forEach((opt) => {
                  if (opt.count > 0) {
                    chips.push({
                      key: `scope-${opt.key}`,
                      label: opt.label,
                      count: opt.count,
                      active: connScopeFilter.has(opt.key),
                      onClick: () => toggleScopeFilter(opt.key),
                    });
                  }
                });
                if (chips.length === 0) return null;
                return (
                  <div
                    className="flex flex-wrap items-center gap-1.5"
                    data-testid={`chips-ttlock-quick-filters-${conn.id}`}
                  >
                    {chips.map((chip) => (
                      <button
                        key={chip.key}
                        type="button"
                        onClick={chip.onClick}
                        aria-pressed={chip.active}
                        className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs transition-colors ${
                          chip.active
                            ? "border-brand bg-brand text-white hover:bg-brand-hover"
                            : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                        }`}
                        data-testid={`chip-ttlock-${chip.key}-${conn.id}`}
                      >
                        {chip.icon}
                        <span className="font-medium">{chip.count}</span>
                        <span>{chip.label}</span>
                      </button>
                    ))}
                  </div>
                );
              })()}
              {filteredSortedLocks.length === 0 && (
                <div className="text-center py-6 bg-white border border-dashed border-gray-200 rounded-lg">
                  <Filter className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No locks match the current filters.</p>
                  <button
                    type="button"
                    onClick={clearConnFilters}
                    className="text-xs text-brand hover:underline mt-1"
                    data-testid={`button-ttlock-clear-filters-${conn.id}`}
                  >
                    Clear filters
                  </button>
                </div>
              )}
              {sortedGroupNames.map((group) => {
                const groupLocks = groupedLocks[group];
                const currentGroupPropId = groupPropertyIds[group] ?? "none";
                const groupPropertySelectLabel =
                  currentGroupPropId === "none"
                    ? "Unassigned"
                    : activeProperties.find((p) => String(p.id) === currentGroupPropId)?.name;
                const isSaving = groupSaving[group] ?? false;
                const multipleGroups = sortedGroupNames.length > 1;
                return (
                  <div key={group}>
                    <div className="flex items-center gap-2 mb-2">
                      {multipleGroups && (
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider flex-shrink-0">{group}</p>
                      )}
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide flex-shrink-0">Property</span>
                        <Select
                          value={currentGroupPropId}
                          onValueChange={(val) => handleGroupPropertyChange(group, val)}
                          disabled={readOnly || isSaving}
                        >
                          <SelectTrigger
                            className="h-6 text-xs w-40 border-gray-300"
                            data-testid={`select-group-property-${conn.id}-${group}`}
                            title={groupPropertySelectLabel || undefined}
                          >
                            {isSaving ? (
                              <span className="text-gray-400">Saving…</span>
                            ) : (
                              <SelectValue placeholder="Unassigned" />
                            )}
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Unassigned</SelectItem>
                            <SelectItem value="ignore-all">Ignore property not on PadSplit</SelectItem>
                            {activeProperties.map((p) => (
                              <SelectItem key={p.id} value={String(p.id)}>
                                {p.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 px-3 mb-1">
                      <div className="w-4 flex-shrink-0" />
                      <div className="flex-1 min-w-0" />
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide w-72 text-center">
                          Property
                        </span>
                        <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide w-72 text-center">
                          Room
                        </span>
                        <div className="w-[4.5rem]" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      {groupLocks.map((lock) => (
                        <TtlockLockRow
                          key={lock.ttlockLockId}
                          lock={lock}
                          activeProperties={activeProperties}
                          readOnly={readOnly}
                          groupPropertyId={currentGroupPropId}
                          onAssign={(ttlockLockId, propertyId, roomId, accessScope) =>
                            assignMutation.mutate({ ttlockLockId, propertyId, roomId, accessScope })
                          }
                          onActivityOpen={() => onActivityOpen({
                            connectionId: conn.id,
                            ttlockLockId: lock.ttlockLockId,
                            name: lock.lockAlias || lock.lockName || `Lock ${lock.ttlockLockId}`,
                          })}
                          onIgnore={(ttlockLockId, ignored) =>
                            ignoreMutation.mutate({ ttlockLockId, ignored })
                          }
                        />
                      ))}
                    </div>
                  </div>
                );
              })}

              {/* Task #1729 — Ignored locks section */}
              {ignoredSortedLocks.length > 0 && (
                <div className="mt-4 border-t border-gray-100 pt-3">
                  <div className="flex items-center justify-between mb-2">
                    <button
                      type="button"
                      className="flex items-center gap-2 text-xs text-gray-400 hover:text-gray-600"
                      onClick={() => setShowIgnored((v) => !v)}
                      data-testid={`button-toggle-ignored-locks-${conn.id}`}
                    >
                      <ChevronRight className={`h-3.5 w-3.5 transition-transform ${showIgnored ? "rotate-90" : ""}`} />
                      {ignoredSortedLocks.length} ignored lock{ignoredSortedLocks.length !== 1 ? "s" : ""} (not used for PadSplit)
                    </button>
                    {!readOnly && (
                      <button
                        type="button"
                        className="text-xs text-blue-500 hover:text-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={handleBulkUnignore}
                        disabled={bulkUnignoreSaving}
                        data-testid={`button-restore-all-ignored-locks-${conn.id}`}
                      >
                        {bulkUnignoreSaving ? "Saving…" : "Restore all"}
                      </button>
                    )}
                  </div>
                  {showIgnored && (
                    <div className="space-y-2">
                      {ignoredSortedLocks.map((lock) => (
                        <TtlockLockRow
                          key={lock.ttlockLockId}
                          lock={lock}
                          activeProperties={activeProperties}
                          readOnly={readOnly}
                          onAssign={(ttlockLockId, propertyId, roomId, accessScope) =>
                            assignMutation.mutate({ ttlockLockId, propertyId, roomId, accessScope })
                          }
                          onActivityOpen={() => onActivityOpen({
                            connectionId: conn.id,
                            ttlockLockId: lock.ttlockLockId,
                            name: lock.lockAlias || lock.lockName || `Lock ${lock.ttlockLockId}`,
                          })}
                          onIgnore={(ttlockLockId, ignored) =>
                            ignoreMutation.mutate({ ttlockLockId, ignored })
                          }
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TtlockActivityDrawer({
  lock,
  onClose,
}: {
  lock: { connectionId: number; ttlockLockId: number; name: string } | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 30;

  useEffect(() => { if (lock) setPage(0); }, [lock]);

  const recordsQuery = useQuery<{ records: TtlockUnlockRecord[]; limit: number; offset: number; lastSyncedAt: string | null }>({
    queryKey: ["/api/app/devices/ttlock/records", lock?.connectionId, lock?.ttlockLockId, page],
    queryFn: async () => {
      if (!lock) return { records: [], limit: PAGE_SIZE, offset: 0, lastSyncedAt: null };
      const offset = page * PAGE_SIZE;
      const res = await fetch(
        `/api/app/devices/ttlock/locks/${lock.ttlockLockId}/records?connectionId=${lock.connectionId}&limit=${PAGE_SIZE}&offset=${offset}`,
        { credentials: "include" },
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        if (data.code === "NO_GATEWAY") return { records: [], limit: PAGE_SIZE, offset: 0, lastSyncedAt: null, noGateway: true } as any;
        return { records: [], limit: PAGE_SIZE, offset: 0, lastSyncedAt: null };
      }
      return res.json();
    },
    enabled: !!lock,
    refetchInterval: lock ? 60_000 : false,
  });

  const responseData = recordsQuery.data as any;
  const noGateway = responseData?.noGateway === true;
  const records = responseData?.records || [];
  const lastSyncedAt: string | null = responseData?.lastSyncedAt ?? null;
  const hasMore = records.length >= PAGE_SIZE;

  const [, setNowTick] = useState(0);
  useEffect(() => {
    if (!lock || !lastSyncedAt) return;
    const id = setInterval(() => setNowTick((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, [lock, lastSyncedAt]);
  const lastSyncedLabel = formatRelativeSync(lastSyncedAt);

  const refreshMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async () => {
      if (!lock) throw new Error("No lock selected");
      const res = await fetch(
        `/api/app/devices/ttlock/locks/${lock.ttlockLockId}/records/sync`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ connectionId: lock.connectionId }),
        },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const err = new Error(data?.message || "Failed to refresh records") as Error & { code?: string };
        err.code = data?.code;
        throw err;
      }
      return data as { success: true; inserted: number };
    },
    onSuccess: (data) => {
      if (lock) {
        queryClient.invalidateQueries({
          queryKey: ["/api/app/devices/ttlock/records", lock.connectionId, lock.ttlockLockId],
        });
      }
      setPage(0);
      toast({
        title: data.inserted > 0
          ? `Found ${data.inserted} new record${data.inserted === 1 ? "" : "s"}`
          : "Already up to date",
      });
    },
    onError: (err: Error & { code?: string }) => {
      toast({
        title: err.code === "RATE_LIMITED" ? "Slow down" : "Refresh failed",
        description: parseErrorMessage(err, "Couldn't refresh lock records. Please try again."),
        variant: "destructive",
      });
    },
  });

  const refreshDisabled = !lock || noGateway || refreshMutation.isPending || recordsQuery.isLoading;

  return (
    <Sheet open={!!lock} onOpenChange={(open) => { if (!open) onClose(); }}>
      <SheetContent side="right" className="w-full sm:w-[420px] overflow-y-auto">
        <SheetHeader className="mb-4">
          <div className="flex items-center justify-between gap-2">
            <SheetTitle className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-gray-500" />
              Activity — {lock?.name}
            </SheetTitle>
            <div className="flex items-center gap-2">
              {lastSyncedLabel && !noGateway && (
                <span
                  className="text-xs text-gray-500"
                  title={lastSyncedAt ? new Date(lastSyncedAt).toLocaleString() : undefined}
                  data-testid="text-records-last-synced"
                >
                  Last updated {lastSyncedLabel}
                </span>
              )}
              <Button
                variant="outline"
                size="sm"
                className="text-xs"
                disabled={refreshDisabled}
                onClick={() => refreshMutation.mutate()}
                data-testid="button-refresh-records"
              >
                <RefreshCw className={`h-3.5 w-3.5 mr-1 ${refreshMutation.isPending ? "animate-spin" : ""}`} />
                {refreshMutation.isPending ? "Refreshing…" : "Refresh"}
              </Button>
            </div>
          </div>
        </SheetHeader>
        {recordsQuery.isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="bg-gray-50 rounded-lg p-3 animate-pulse h-14" />
            ))}
          </div>
        ) : noGateway ? (
          <div className="text-center py-12 px-4">
            <Wifi className="h-10 w-10 text-amber-400 mx-auto mb-3" />
            <p className="text-sm font-medium text-gray-700">This lock requires a TTLock Gateway to record activity</p>
            <p className="text-xs text-gray-500 mt-2 leading-relaxed">
              The TTLock cloud only stores unlock history when a Gateway (Wi-Fi bridge) is paired with the lock. Without one, events stay on the lock and can't be retrieved remotely.
            </p>
            <a
              href="https://www.ttlock.com/en/category/Gateway"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 mt-4 text-xs font-medium text-blue-600 hover:text-blue-700"
              data-testid="link-ttlock-gateway-accessories"
            >
              Browse TTLock Gateway accessories
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        ) : records.length === 0 && page === 0 ? (
          <div className="text-center py-12">
            <Clock className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500">No unlock records yet.</p>
            <p className="text-xs text-gray-400 mt-1">Records will appear after the next 15-minute sync.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {records.map((rec: TtlockUnlockRecord) => (
              <div
                key={rec.id}
                className="bg-white border border-gray-200 rounded-lg px-3 py-2.5"
                data-testid={`row-unlock-record-${rec.id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${rec.success === 1 ? "bg-green-50" : "bg-red-50"}`}>
                      {rec.success === 1
                        ? <LockOpen className="h-3.5 w-3.5 text-green-600" />
                        : <Lock className="h-3.5 w-3.5 text-red-500" />
                      }
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {rec.recordTypeLabel || "Unknown method"}
                        </p>
                        {rec.source === "webhook" && (
                          <Badge
                            variant="outline"
                            className="text-[10px] px-1.5 py-0 h-4 border-green-200 bg-green-50 text-green-700 font-medium flex-shrink-0"
                            data-testid={`badge-record-source-live-${rec.id}`}
                            title="Delivered in real time via TTLock webhook"
                          >
                            Live
                          </Badge>
                        )}
                        {rec.source === "poll" && (
                          <Badge
                            variant="outline"
                            className="text-[10px] px-1.5 py-0 h-4 border-gray-200 bg-gray-50 text-gray-600 font-medium flex-shrink-0"
                            data-testid={`badge-record-source-polled-${rec.id}`}
                            title="Fetched by the 15-minute TTLock API poller"
                          >
                            Polled
                          </Badge>
                        )}
                      </div>
                      {rec.username && <p className="text-xs text-gray-500 truncate">{rec.username}</p>}
                    </div>
                  </div>
                  {rec.recordTime && (
                    <span className="text-xs text-gray-400 flex-shrink-0 text-right">
                      {new Date(rec.recordTime).toLocaleDateString()}<br />
                      {new Date(rec.recordTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </span>
                  )}
                </div>
              </div>
            ))}
            <div className="flex gap-2 pt-2">
              {page > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 text-xs"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  data-testid="button-records-prev"
                >
                  ← Previous
                </Button>
              )}
              {hasMore && (
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 text-xs"
                  onClick={() => setPage((p) => p + 1)}
                  data-testid="button-records-next"
                >
                  Load more →
                </Button>
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

/**
 * Task #2242 — Per-lock Property/Room picker for a Seam lock. Mirrors
 * TtlockLockRow's picker behavior: selecting a property un-sets the room,
 * selecting "Unassigned" clears both, and the room dropdown is disabled
 * until a property is picked. Wired to PATCH /api/app/devices/seam/locks/:id.
 */
function SeamLockAssignmentControls({
  lock,
  activeProperties,
  readOnly,
  onAssign,
}: {
  lock: any;
  activeProperties: Property[];
  readOnly: boolean;
  onAssign: (deviceId: string, propertyId: number | null, roomId: number | null) => void;
}) {
  // Initial values come from the explicit pin first, then fall back to the
  // server-derived name-matched property id so the picker doesn't lie about
  // a lock that's already auto-included by the legacy heuristic.
  const initialPropertyId: number | null =
    lock.__padpulse_property_id ??
    (lock.__padpulse_room_id != null ? null : lock.__padpulse_name_matched_property_id ?? null);
  const initialRoomId: number | null = lock.__padpulse_room_id ?? null;
  const propertyValue = initialPropertyId != null ? String(initialPropertyId) : "none";
  const propertySelectLabel =
    propertyValue === "none"
      ? "Unassigned"
      : activeProperties.find((p) => String(p.id) === propertyValue)?.name;
  const roomValue = initialRoomId != null ? String(initialRoomId) : (initialPropertyId != null ? "common" : "none");

  const propertyIdNum = initialPropertyId;
  const roomsQuery = useQuery<Room[]>({
    queryKey: ["/api/app/properties", propertyIdNum, "rooms"],
    queryFn: async () => {
      if (!propertyIdNum) return [];
      const res = await fetch(`/api/app/properties/${propertyIdNum}/rooms`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!propertyIdNum,
    staleTime: 60_000,
  });

  const filteredRooms = (roomsQuery.data || []).filter(
    (r) => r.roomType !== "bathroom" && !r.roomNumber?.toLowerCase().startsWith("bath"),
  );

  const roomSelectValue = propertyValue === "none" ? "none" : roomValue;
  const roomSelectLabel =
    roomSelectValue === "none"
      ? "None (not a PadSplit room)"
      : roomSelectValue === "common"
        ? "Common space"
        : (() => {
            const r = filteredRooms.find((room) => String(room.id) === roomSelectValue);
            return r ? r.roomNumber || (r as any).name || `Room ${r.id}` : undefined;
          })();

  const handlePropertyChange = (val: string) => {
    const newPropId = val === "none" ? null : parseInt(val, 10);
    // Switching property always clears the specific room; "Unassigned"
    // clears both fields.
    onAssign(lock.device_id, newPropId, null);
  };

  const handleRoomChange = (val: string) => {
    if (propertyIdNum == null) return;
    if (val === "none") {
      onAssign(lock.device_id, null, null);
    } else if (val === "common") {
      onAssign(lock.device_id, propertyIdNum, null);
    } else {
      onAssign(lock.device_id, null, parseInt(val, 10));
    }
  };

  return (
    <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
      <SearchableSelect
        value={propertyValue}
        onValueChange={handlePropertyChange}
        disabled={readOnly}
        placeholder="Property"
        searchPlaceholder="Search properties..."
        emptyText="No properties match."
        testId={`select-seam-property-${lock.device_id}`}
        className="h-6 text-xs w-72 border-gray-200"
        triggerProps={{ title: propertySelectLabel || undefined }}
        options={[
          { value: "none", label: "Unassigned" },
          ...activeProperties.map((p) => ({
            value: String(p.id),
            label: p.name,
            keywords: p.address ? `${p.name} ${p.address}` : undefined,
          })),
        ]}
      />
      <Select
        value={propertyValue === "none" ? "none" : roomValue}
        onValueChange={handleRoomChange}
        disabled={readOnly || propertyValue === "none"}
      >
        <SelectTrigger
          className="h-6 text-xs w-72 border-gray-200"
          data-testid={`select-seam-room-${lock.device_id}`}
          title={roomSelectLabel || undefined}
        >
          <SelectValue placeholder="Room or area" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">None (not a PadSplit room)</SelectItem>
          <SelectItem value="common">Common space</SelectItem>
          {filteredRooms.map((r) => (
            <SelectItem key={r.id} value={String(r.id)}>
              {r.roomNumber || (r as any).name || `Room ${r.id}`}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/**
 * Task #1729 / #1733 / #2242 — per-connection Seam lock listing with
 * property/room assignment pickers (Task #2242) plus ignore/unignore
 * controls. Mirrors TTLock's TtlockLockRow UI for the Seam provider.
 */
function SeamConnectionLocksPanel({
  connectionId,
  readOnly,
  activeProperties,
  onIgnore,
  onAssign,
}: {
  connectionId: number;
  readOnly: boolean;
  activeProperties: Property[];
  onIgnore: (deviceId: string, ignored: boolean) => void;
  onAssign: (deviceId: string, propertyId: number | null, roomId: number | null) => void;
}) {
  const locksQuery = useQuery<any[]>({
    queryKey: ["/api/app/devices/seam/locks", connectionId],
    queryFn: async () => {
      const res = await fetch(
        `/api/app/devices/seam/locks?connectionId=${connectionId}`,
        { credentials: "include" },
      );
      if (!res.ok) throw new Error("Failed to load locks");
      return res.json();
    },
    staleTime: 60_000,
  });

  const [showIgnored, setShowIgnored] = useState(false);
  // Task #2422 — low-battery quick filter, mirroring the TTLock connection card.
  const [lowBatteryOnly, setLowBatteryOnly] = useState(false);

  if (locksQuery.isLoading) {
    return <div className="text-xs text-gray-400">Loading locks…</div>;
  }
  if (locksQuery.isError || !locksQuery.data) return null;

  // Task #2422 — derive battery percent from Seam's `properties.battery.level`
  // (0–1 float). Mirrors the conversion in the seam battery-check route and the
  // lock row badge so the filter threshold matches what the host sees.
  const seamBatteryPct = (lock: any): number | null => {
    const level = lock?.properties?.battery?.level;
    if (level === undefined || level === null) return null;
    return Math.round(level * 100);
  };
  const isLowBatterySeamLock = (lock: any): boolean => {
    const pct = seamBatteryPct(lock);
    return pct !== null && pct < 20;
  };

  const allActiveLocks = locksQuery.data.filter((l) => !l.__padpulse_ignored);
  const ignoredLocks = locksQuery.data.filter((l) => l.__padpulse_ignored);
  const lowBatteryCount = allActiveLocks.filter(isLowBatterySeamLock).length;
  const activeLocks = lowBatteryOnly
    ? allActiveLocks.filter(isLowBatterySeamLock)
    : allActiveLocks;
  const hasActiveFilters = lowBatteryOnly;

  if (locksQuery.data.length === 0) {
    return (
      <div className="text-xs text-gray-400">No locks found for this account.</div>
    );
  }

  const renderRow = (lock: any, isIgnored: boolean) => (
    <div
      key={lock.device_id}
      className={`bg-white border rounded-lg px-3 py-2.5 flex items-center gap-3 flex-wrap ${isIgnored ? "border-gray-100 opacity-60" : "border-gray-200"}`}
      data-testid={isIgnored ? `row-seam-lock-ignored-${lock.device_id}` : `row-seam-lock-${lock.device_id}`}
    >
      <Lock className="h-4 w-4 text-gray-400 flex-shrink-0" />
      <div className="flex-1 min-w-0 flex items-center gap-2">
        <p className="text-sm font-medium text-gray-900 truncate">
          {lock.display_name || lock.device_id}
        </p>
        {isIgnored && (
          <Badge
            variant="outline"
            className="text-[9px] px-1.5 py-0 bg-gray-50 text-gray-500 border-gray-200"
            data-testid={`badge-seam-ignored-${lock.device_id}`}
          >
            Ignored
          </Badge>
        )}
        {!isIgnored && lock.__padpulse_unassigned && (
          <Badge
            variant="outline"
            className="text-[9px] px-1.5 py-0 bg-amber-50 text-amber-700 border-amber-200"
            data-testid={`badge-seam-unassigned-${lock.device_id}`}
          >
            Unassigned
          </Badge>
        )}
      </div>
      {!isIgnored && (
        <SeamLockAssignmentControls
          lock={lock}
          activeProperties={activeProperties}
          readOnly={readOnly}
          onAssign={onAssign}
        />
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="p-1 hover:bg-gray-100 rounded"
            data-testid={`button-seam-lock-actions-${lock.device_id}`}
          >
            <MoreHorizontal className="h-3.5 w-3.5 text-gray-400" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuPortal>
          <DropdownMenuContent align="end">
            {!isIgnored && !readOnly && (
              <DropdownMenuItem
                onSelect={() => onIgnore(lock.device_id, true)}
                data-testid={`button-seam-ignore-${lock.device_id}`}
              >
                <Ban className="h-4 w-4 mr-2 text-gray-500" />
                Ignore (not a PadSplit lock)
              </DropdownMenuItem>
            )}
            {isIgnored && !readOnly && (
              <DropdownMenuItem
                onSelect={() => onIgnore(lock.device_id, false)}
                data-testid={`button-seam-restore-${lock.device_id}`}
              >
                <Check className="h-4 w-4 mr-2 text-gray-500" />
                Restore (use for PadSplit)
              </DropdownMenuItem>
            )}
            {isIgnored && (
              <DropdownMenuItem disabled className="text-xs text-gray-400 cursor-default">
                Ignored — excluded from all properties
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenuPortal>
      </DropdownMenu>
    </div>
  );

  return (
    <div className="space-y-2">
      {/* Task #2422 — quick-filter dropdown + chip for low-battery Seam locks,
          mirroring the TTLock connection card's filter UI. */}
      {allActiveLocks.length > 0 && (
        <div className="flex items-center justify-between gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-8"
                data-testid={`button-seam-filter-${connectionId}`}
              >
                <Filter className="h-3.5 w-3.5 mr-1.5" />
                Filter
                {hasActiveFilters && (
                  <Badge
                    variant="secondary"
                    className="ml-1.5 h-4 min-w-[18px] px-1 text-[10px] bg-brand text-white"
                    data-testid={`badge-seam-filter-count-${connectionId}`}
                  >
                    {lowBatteryOnly ? 1 : 0}
                  </Badge>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              <div className="px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                Battery
              </div>
              <DropdownMenuItem
                onSelect={(e) => { e.preventDefault(); setLowBatteryOnly((v) => !v); }}
                data-testid={`filter-seam-low-battery-${connectionId}`}
                className="flex items-center justify-between cursor-pointer"
              >
                <span className="flex items-center gap-1.5">
                  <BatteryLow className="h-3.5 w-3.5 text-red-500" />
                  Low battery
                </span>
                {lowBatteryOnly && <Check className="h-3.5 w-3.5 text-brand" />}
              </DropdownMenuItem>
              {hasActiveFilters && (
                <DropdownMenuItem
                  onSelect={(e) => { e.preventDefault(); setLowBatteryOnly(false); }}
                  data-testid={`filter-seam-clear-${connectionId}`}
                  className="cursor-pointer text-gray-600 border-t border-gray-100 mt-1"
                >
                  Clear all filters
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          {hasActiveFilters && (
            <span className="text-xs text-gray-500" data-testid={`text-seam-filter-count-${connectionId}`}>
              Showing {activeLocks.length} of {allActiveLocks.length} lock{allActiveLocks.length !== 1 ? "s" : ""}
            </span>
          )}
        </div>
      )}
      {lowBatteryCount > 0 && (
        <div
          className="flex flex-wrap items-center gap-1.5"
          data-testid={`chips-seam-quick-filters-${connectionId}`}
        >
          <button
            type="button"
            onClick={() => setLowBatteryOnly((v) => !v)}
            aria-pressed={lowBatteryOnly}
            className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs transition-colors ${
              lowBatteryOnly
                ? "border-brand bg-brand text-white hover:bg-brand-hover"
                : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
            }`}
            data-testid={`chip-seam-low-battery-${connectionId}`}
          >
            <BatteryLow className="h-3 w-3" />
            <span className="font-medium">{lowBatteryCount}</span>
            <span>low battery</span>
          </button>
        </div>
      )}
      {activeLocks.length === 0 && hasActiveFilters && (
        <div className="text-center py-6 bg-white border border-dashed border-gray-200 rounded-lg">
          <Filter className="h-8 w-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">No locks match the current filters.</p>
          <button
            type="button"
            onClick={() => setLowBatteryOnly(false)}
            className="text-xs text-brand hover:underline mt-1"
            data-testid={`button-seam-clear-filters-${connectionId}`}
          >
            Clear filters
          </button>
        </div>
      )}
      {activeLocks.map((lock) => renderRow(lock, false))}
      {ignoredLocks.length > 0 && (
        <div className="pt-1">
          <button
            type="button"
            className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
            onClick={() => setShowIgnored((v) => !v)}
            data-testid={`button-seam-toggle-ignored-${connectionId}`}
          >
            <ChevronRight className={`h-3 w-3 transition-transform ${showIgnored ? "rotate-90" : ""}`} />
            <span>
              {ignoredLocks.length} ignored lock{ignoredLocks.length !== 1 ? "s" : ""}
            </span>
          </button>
          {showIgnored && (
            <div className="space-y-2 mt-2">
              {ignoredLocks.map((lock) => renderRow(lock, true))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── DeviceSettingsPanel ──────────────────────────────────────────────────────
// Moved here from the Devices overview page — now lives exclusively on the
// Smart Locks page under the Settings tab.

interface DeviceSettingsPayload {
  codeFormat: "random_number";
  codeDigits: number;
  notifyFirstUse: boolean;
  messageLowBattery: boolean;
  lowBatteryThreshold: number;
  lowBatteryNotifyTime: string;
}

// Task #2909 — minimal shape of the account profile so we can show the
// account-level timezone (set in Account/Settings) on the low-battery card.
interface ProfileTimezone {
  timezone: string | null;
}

function DeviceSettingsPanel() {
  const queryClient = useQueryClient();
  const { canEdit: __canEditArea } = usePermissions();
  const readOnly = !__canEditArea("devices");
  const { toast } = useToast();
  // Task #4266 — deep-link target: ?highlight=low_battery scrolls to and
  // flashes the Low Battery Auto-Notification settings block.
  const search = useSearch();
  const highlightLowBattery = new URLSearchParams(search).get("highlight") === "low_battery";
  const lowBatteryRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!highlightLowBattery) return;
    // Wait for the panel to render, then scroll + flash.
    const timer = setTimeout(() => {
      lowBatteryRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      lowBatteryRef.current?.classList.add("ring-2", "ring-brand", "transition-shadow", "duration-500");
      const clearTimer = setTimeout(() => {
        lowBatteryRef.current?.classList.remove("ring-2", "ring-brand");
      }, 2500);
      return () => clearTimeout(clearTimer);
    }, 150);
    return () => clearTimeout(timer);
  }, [highlightLowBattery]);

  const settingsQuery = useQuery<DeviceSettings>({
    queryKey: ["/api/app/devices/settings"],
  });

  // Task #2909 — The reminder timezone is now an account-level setting picked
  // once in Account/Settings. Read it here so the card shows where reminders
  // fire and points the host to where they change it.
  const profileQuery = useQuery<ProfileTimezone>({
    queryKey: ["/api/app/profile"],
  });
  const accountTimezone = profileQuery.data?.timezone || DEFAULT_ACCOUNT_TIMEZONE;

  const [codeDigits, setCodeDigits] = useState<number>(4);
  const [messageLowBattery, setMessageLowBattery] = useState<boolean>(false);
  const [lowBatteryThreshold, setLowBatteryThreshold] = useState<number>(20);
  const [lowBatteryNotifyTime, setLowBatteryNotifyTime] = useState<string>("17:00");
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (settingsQuery.data && !initialized) {
      setCodeDigits(settingsQuery.data.codeDigits ?? 4);
      setMessageLowBattery(settingsQuery.data.messageLowBattery ?? false);
      setLowBatteryThreshold(settingsQuery.data.lowBatteryThreshold ?? 20);
      setLowBatteryNotifyTime(settingsQuery.data.lowBatteryNotifyTime ?? "17:00");
      setInitialized(true);
    }
  }, [settingsQuery.data, initialized]);

  const saveMutation = useMutation({
    mutationFn: async (data: DeviceSettingsPayload) => {
      return apiRequest("POST", "/api/app/devices/settings", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/settings"] });
      toast({ title: "Settings saved", description: "Your device settings have been updated." });
    },
    onError: () => {
      toast({ title: "Failed to save", description: "Could not save settings.", variant: "destructive" });
    },
  });

  const handleSave = () => {
    saveMutation.mutate({
      codeFormat: "random_number",
      codeDigits,
      messageLowBattery,
      lowBatteryThreshold,
      lowBatteryNotifyTime,
      notifyFirstUse: settingsQuery.data?.notifyFirstUse ?? false,
    });
  };

  if (settingsQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="max-w-lg space-y-8">
      <div className="border border-gray-200 rounded-lg p-6" data-tour="smart-locks-settings-code-length">
        <div className="flex items-center gap-2 mb-4">
          <KeyRound className="h-4 w-4 text-brand" />
          <h3 className="text-sm font-semibold text-gray-900">Default Code Length</h3>
        </div>
        <p className="text-xs text-gray-500 mb-4">
          Number of digits used when auto-generating access codes for new members.
        </p>
        <div className="flex gap-2">
          {[4, 5, 6].map((digits) => (
            <button
              key={digits}
              onClick={() => setCodeDigits(digits)}
              className={`flex-1 py-2.5 text-sm rounded-md border transition-colors font-medium ${
                codeDigits === digits
                  ? "bg-brand text-white border-brand"
                  : "bg-white text-gray-600 border-gray-200 hover:border-brand/40"
              }`}
              data-testid={`button-code-digits-${digits}`}
            >
              {digits}-digit
            </button>
          ))}
        </div>
      </div>

      <div
        ref={lowBatteryRef}
        id="low-battery-settings"
        className="border border-gray-200 rounded-lg p-6"
        data-tour="smart-locks-settings-low-battery"
      >
        <div className="flex items-center gap-2 mb-4">
          <BatteryLow className="h-4 w-4 text-red-500" />
          <h3 className="text-sm font-semibold text-gray-900">Low Battery Auto-Notification</h3>
        </div>
        <p className="text-xs text-gray-500 mb-4">
          Automatically message the member associated with a lock when its battery drops below the configured threshold. A reminder is sent daily at the configured time until the battery is replaced.
        </p>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900">Enable notifications</p>
              <p className="text-xs text-gray-500">Send a message when battery is low</p>
            </div>
            <Switch
              checked={messageLowBattery}
              onCheckedChange={setMessageLowBattery}
              data-testid="switch-low-battery"
            />
          </div>

          {messageLowBattery && (
            <div className="ml-0 space-y-2">
              <label className="text-sm font-medium text-gray-900 block">
                Battery threshold
              </label>
              <p className="text-xs text-gray-500">
                Notify when battery level drops below this percentage.
              </p>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min={1}
                  max={100}
                  value={lowBatteryThreshold}
                  onChange={(e) => setLowBatteryThreshold(Math.min(100, Math.max(1, parseInt(e.target.value) || 20)))}
                  className="w-24 font-mono"
                  data-testid="input-battery-threshold"
                />
                <span className="text-sm text-gray-500">%</span>
              </div>
            </div>
          )}

          <div className="ml-0 space-y-2">
            <label className={`text-sm font-medium block ${messageLowBattery ? "text-gray-900" : "text-gray-400"}`}>
              Notification time
            </label>
            <p className={`text-xs ${messageLowBattery ? "text-gray-500" : "text-gray-400"}`}>
              Time of day when the daily low-battery reminder is sent, in your account timezone. Repeats every day until the battery is replaced.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <Select
                value={lowBatteryNotifyTime}
                onValueChange={setLowBatteryNotifyTime}
                disabled={!messageLowBattery || readOnly}
              >
                <SelectTrigger className="w-40" data-testid="select-notify-time">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 48 }, (_, i) => {
                    const hh = Math.floor(i / 2).toString().padStart(2, "0");
                    const mm = i % 2 === 0 ? "00" : "30";
                    const value = `${hh}:${mm}`;
                    const hour12 = Math.floor(i / 2) % 12 || 12;
                    const ampm = Math.floor(i / 2) < 12 ? "AM" : "PM";
                    const label = `${hour12}:${mm} ${ampm}`;
                    return (
                      <SelectItem key={value} value={value} data-testid={`option-notify-time-${value}`}>
                        {label}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <p className={`text-xs ${messageLowBattery ? "text-gray-500" : "text-gray-400"}`} data-testid="text-notify-timezone-summary">
              Reminders fire at {(() => {
                const [h, m] = lowBatteryNotifyTime.split(":").map(Number);
                const hour12 = h % 12 || 12;
                const ampm = h < 12 ? "AM" : "PM";
                return `${hour12}:${m.toString().padStart(2, "0")} ${ampm}`;
              })()} {timezoneLabel(accountTimezone)} — your account timezone.{" "}
              <Link
                href="/app/profile"
                className="text-brand hover:underline"
                data-testid="link-account-timezone-settings"
              >
                Change it in Account settings
              </Link>
              .
            </p>
          </div>
        </div>
      </div>

      <Button
        onClick={handleSave}
        disabled={readOnly || saveMutation.isPending}
        className="bg-brand hover:bg-brand-hover text-white"
        data-testid="button-save-device-settings"
      >
        {saveMutation.isPending ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Saving...
          </>
        ) : (
          "Save Settings"
        )}
      </Button>
    </div>
  );
}

// ─── SmartLocksPropertyBrowser ────────────────────────────────────────────────
// Self-contained property → locks → lock-detail browser, exported so it can be
// embedded in the Devices overview page under the Smart Locks tab.

// ─── LockDetailDrawer ─────────────────────────────────────────────────────────

export interface LockDetailDrawerProps {
  selectedLock: UnifiedLock | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  readOnly?: boolean;
  selectedPropertyId?: number | null;
  /** Tab to show when the drawer opens (closed → open). Defaults to Access Codes. */
  initialTab?: DrawerTab;
}

/**
 * Self-contained lock-detail slide-out drawer. Extracted from
 * SmartLocksPropertyBrowser so it can be mounted in-place from any tab
 * (e.g. the Members tab on the Devices overview page) without switching to the
 * Smart Locks tab. All queries and mutations live inside this component.
 */
export function LockDetailDrawer({
  selectedLock,
  open,
  onOpenChange,
  readOnly = false,
  selectedPropertyId = null,
  initialTab = "access_codes",
}: LockDetailDrawerProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [, navigateLocation] = useLocation();

  const [drawerTab, setDrawerTab] = useState<DrawerTab>("access_codes");
  const [drawerActivityPage, setDrawerActivityPage] = useState(0);
  const [showCodeDialog, setShowCodeDialog] = useState(false);
  const [codeDialogLock, setCodeDialogLock] = useState<UnifiedLock | null>(null);
  const [editingCode, setEditingCode] = useState<AccessCode | null>(null);
  const [codeForm, setCodeForm] = useState({ name: "", code: "", startsAt: "", endsAt: "" });

  // Reset to the requested initial tab whenever the drawer transitions
  // closed → open (defaults to Access Codes; deep-links can request Activity).
  // The prevOpenRef guard ensures a mid-open initialTab change never resets it.
  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setDrawerTab(initialTab);
      setDrawerActivityPage(0);
    }
    prevOpenRef.current = open;
  }, [open, initialTab]);

  // Clean up dialog state when the drawer fully closes.
  useEffect(() => {
    if (!open) {
      setCodeDialogLock(null);
      setEditingCode(null);
      setCodeForm({ name: "", code: "", startsAt: "", endsAt: "" });
      setShowCodeDialog(false);
    }
  }, [open]);

  // The Battery Alert Recipient list must resolve to the lock's OWN property,
  // not the browser-navigation property. When a lock is opened from a
  // cross-property view (e.g. the low-battery filter), the page-level
  // `selectedPropertyId` is null, which previously disabled the members query
  // and left the dropdown blank. Fall back to the lock's own propertyId.
  const effectivePropertyId = selectedLock?.propertyId ?? selectedPropertyId ?? null;

  const membersQuery = useQuery<Array<Member & { currentPropertyId?: number | null }>>({
    queryKey: ["/api/app/devices/members", effectivePropertyId],
    queryFn: async () => {
      const url = effectivePropertyId
        ? `/api/app/devices/members?propertyId=${effectivePropertyId}`
        : "/api/app/devices/members";
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch members");
      return res.json();
    },
    enabled: !!(open && effectivePropertyId),
  });

  const assignmentsQuery = useQuery<{ assignments: LockMemberAssignment[]; defaults: Record<string, number | null> }>({
    queryKey: ["/api/app/devices/lock-member-assignments"],
    enabled: open,
  });

  const mutesQuery = useQuery<LockHealthMute[]>({
    queryKey: ["/api/app/devices/lock-health-mutes"],
    enabled: open,
  });

  const setAssignmentMutation = useMutation({
    mutationFn: async ({ deviceId, memberId }: { deviceId: string; memberId: number }) => {
      return apiRequest("PUT", `/api/app/devices/lock-member-assignments/${encodeURIComponent(deviceId)}`, { memberId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/lock-member-assignments"] });
      toast({ title: "Member assigned", description: "Battery alerts for this lock will go to this member." });
    },
    onError: () => {
      toast({ title: "Failed to assign member", variant: "destructive" });
    },
  });

  const clearAssignmentMutation = useMutation({
    mutationFn: async (deviceId: string) => {
      return apiRequest("DELETE", `/api/app/devices/lock-member-assignments/${encodeURIComponent(deviceId)}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/lock-member-assignments"] });
      toast({ title: "Assignment cleared", description: "Battery alerts will fall back to all property members." });
    },
    onError: () => {
      toast({ title: "Failed to clear assignment", variant: "destructive" });
    },
  });

  const createMuteMutation = useMutation({
    mutationFn: async (input: { deviceId?: string; propertyId?: number; durationHours: 0 | 24 | 168 }) => {
      return apiRequest("POST", "/api/app/devices/lock-health-mutes", {
        deviceId: input.deviceId ?? null,
        propertyId: input.propertyId ?? null,
        condition: input.propertyId ? "all" : "low_battery",
        durationHours: input.durationHours,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/lock-health-mutes"] });
      toast({ title: "Alerts muted", description: "Low-battery notifications will be silenced for the chosen window." });
    },
    onError: (err: unknown) => {
      toast({ title: extractErrMsg(err, "Failed to mute alerts"), variant: "destructive" });
    },
  });

  const deleteMuteMutation = useMutation({
    mutationFn: async (id: number) => {
      return apiRequest("DELETE", `/api/app/devices/lock-health-mutes/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/lock-health-mutes"] });
      toast({ title: "Alerts unmuted", description: "You'll start receiving low-battery notifications again." });
    },
    onError: () => {
      toast({ title: "Failed to unmute alerts", variant: "destructive" });
    },
  });

  const seamIgnoreMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async ({ deviceId, ignored }: { deviceId: string; ignored: boolean }) => {
      return apiRequest("PUT", `/api/app/devices/seam/locks/${encodeURIComponent(deviceId)}/ignore`, { ignored });
    },
    onSuccess: (_data, { ignored }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/seam/locks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/locks/list-for-property"] });
      toast({ title: ignored ? "Lock excluded from PadSplit" : "Lock restored" });
    },
    onError: (err: unknown) => {
      toast({ title: extractErrMsg(err, "Failed to update lock"), variant: "destructive" });
    },
  });

  const lockControlMutation = useMutation({
    mutationFn: async ({ deviceId, action, connectionId }: { deviceId: string; action: "lock" | "unlock"; connectionId?: number }) => {
      return apiRequest("POST", "/api/app/devices/locks/control", { deviceId, action, connectionId });
    },
    onSuccess: (_, { action }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/locks/list-for-property", selectedPropertyId] });
      toast({ title: action === "lock" ? "Lock command sent" : "Unlock command sent" });
    },
    onError: () => {
      toast({ title: "Failed to send lock command", variant: "destructive" });
    },
  });

  type AccessCodesResponse =
    | { codes: SeamRawAccessCode[]; lastSyncedAt: string | null }
    | { codes: TtlockRawPasscode[]; lastSyncedAt: string | null };

  const lockAccessCodesQuery = useQuery<AccessCodesResponse>({
    queryKey: ["/api/app/devices/lock-access-codes", selectedLock?.id ?? null],
    queryFn: async () => {
      if (!selectedLock) return { codes: [], lastSyncedAt: null } as AccessCodesResponse;
      if (selectedLock.provider === "seam") {
        const url = `/api/app/devices/seam/access-codes?connectionId=${selectedLock.connectionId}&deviceId=${encodeURIComponent(selectedLock.id)}`;
        const res = await fetch(url, { credentials: "include" });
        if (!res.ok) {
          const body = await res.json().catch(() => ({})) as { message?: string };
          throw new Error(body.message || "Failed to fetch access codes");
        }
        return res.json() as Promise<{ codes: SeamRawAccessCode[]; lastSyncedAt: string | null }>;
      } else if (selectedLock.provider === "ttlock") {
        if (!selectedLock.rawLockId) return { codes: [], lastSyncedAt: null } as AccessCodesResponse;
        const url = `/api/app/devices/ttlock/access-codes?connectionId=${selectedLock.connectionId}&lockId=${selectedLock.rawLockId}`;
        const res = await fetch(url, { credentials: "include" });
        if (!res.ok) {
          const body = await res.json().catch(() => ({})) as { message?: string };
          const err = new Error(body.message || "Failed to fetch access codes") as Error & { status?: number };
          err.status = res.status;
          throw err;
        }
        return res.json() as Promise<{ codes: TtlockRawPasscode[]; lastSyncedAt: string | null }>;
      }
      return { codes: [], lastSyncedAt: null } as AccessCodesResponse;
    },
    enabled: !!(selectedLock?.connectionId) && (selectedLock?.provider !== "ttlock" || selectedLock?.rawLockId != null),
  });

  const accessCodesLastSyncedAt: string | null = lockAccessCodesQuery.data?.lastSyncedAt ?? null;
  const [, setAccessCodesNowTick] = useState(0);
  useEffect(() => {
    if (!accessCodesLastSyncedAt) return;
    const id = setInterval(() => setAccessCodesNowTick((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, [accessCodesLastSyncedAt]);
  const accessCodesLastSyncedLabel = formatRelativeSync(accessCodesLastSyncedAt);

  const realAccessCodes: AccessCode[] = (() => {
    if (!selectedLock || !lockAccessCodesQuery.data) return [];
    const codes = lockAccessCodesQuery.data.codes ?? [];
    if (selectedLock.provider === "seam") {
      return (codes as SeamRawAccessCode[]).map((c) => ({
        id: c.access_code_id,
        name: c.name,
        code: c.code ?? "••••",
        type: "member" as const,
        status: (c.status === "set" || c.status === "setting") ? "active" as const : "inactive" as const,
        createdAt: c.created_at ?? "",
      }));
    } else {
      return (codes as TtlockRawPasscode[]).map((p) => ({
        id: String(p.keyboardPwdId),
        name: p.keyboardPwdName,
        code: p.keyboardPwd,
        type: "member" as const,
        status: "active" as const,
        createdAt: "",
      }));
    }
  })();

  const revokeAccessCodeMutation = useMutation({
    mutationFn: async ({ codeId }: { codeId: string }) => {
      if (!selectedLock) throw new Error("No lock selected");
      if (selectedLock.provider === "seam") {
        const res = await fetch(
          `/api/app/devices/seam/access-codes/${encodeURIComponent(codeId)}?connectionId=${selectedLock.connectionId}`,
          { method: "DELETE", credentials: "include" },
        );
        if (!res.ok) throw new Error("Failed to delete access code");
        return res.json();
      } else {
        const res = await fetch(
          `/api/app/devices/ttlock/access-codes/${encodeURIComponent(codeId)}?connectionId=${selectedLock.connectionId}&lockId=${selectedLock.rawLockId}`,
          { method: "DELETE", credentials: "include" },
        );
        if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(extractErrMsg(e, "Failed to delete TTLock access code")); }
        return res.json();
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/lock-access-codes", selectedLock?.id] });
      toast({ title: "Access code removed" });
    },
    onError: (err: unknown) => {
      toast({ title: extractErrMsg(err, "Failed to remove access code"), variant: "destructive" });
    },
  });

  interface AutoLockState {
    supported: boolean;
    enabled: boolean;
    delaySec: number;
  }

  const autoLockQuery = useQuery<AutoLockState>({
    queryKey: ["/api/app/devices/auto-lock", selectedLock?.provider, selectedLock?.connectionId, selectedLock?.rawLockId ?? selectedLock?.id],
    queryFn: async () => {
      if (!selectedLock?.connectionId) return { supported: false, enabled: false, delaySec: 0 };
      if (selectedLock.provider === "seam") {
        const res = await fetch(
          `/api/app/devices/seam/auto-lock?connectionId=${selectedLock.connectionId}&deviceId=${encodeURIComponent(selectedLock.id)}`,
          { credentials: "include" },
        );
        if (!res.ok) {
          const body = await res.json().catch(() => ({})) as { message?: string; supported?: boolean | null };
          if (body.supported === null) {
            throw new Error(body.message || "Failed to fetch auto-lock state from Seam");
          }
          return { supported: false, enabled: false, delaySec: 0 };
        }
        return res.json() as Promise<AutoLockState>;
      } else if (selectedLock.provider === "ttlock") {
        if (!selectedLock.rawLockId) return { supported: false, enabled: false, delaySec: 0 };
        const res = await fetch(
          `/api/app/devices/ttlock/auto-lock?connectionId=${selectedLock.connectionId}&lockId=${selectedLock.rawLockId}`,
          { credentials: "include" },
        );
        if (!res.ok) {
          const body = await res.json().catch(() => ({})) as { message?: string; supported?: boolean | null };
          if (body.supported === null) {
            throw new Error(body.message || "Failed to fetch auto-lock state from TTLock");
          }
          return { supported: false, enabled: false, delaySec: 0 };
        }
        return res.json() as Promise<AutoLockState>;
      }
      return { supported: false, enabled: false, delaySec: 0 };
    },
    enabled: !!(open && drawerTab === "manage" && selectedLock?.connectionId),
    staleTime: 30_000,
  });

  const createAccessCodeMutation = useMutation({
    mutationFn: async () => {
      const lock = codeDialogLock ?? selectedLock;
      if (!lock) throw new Error("No lock selected");
      if (lock.provider === "seam") {
        const res = await fetch("/api/app/devices/seam/access-codes", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            connectionId: lock.connectionId,
            deviceId: lock.id,
            name: codeForm.name,
            code: codeForm.code,
            ...(codeForm.startsAt ? { startsAt: codeForm.startsAt } : {}),
            ...(codeForm.endsAt ? { endsAt: codeForm.endsAt } : {}),
          }),
        });
        if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(extractErrMsg(e, "Failed to create access code")); }
        return res.json();
      } else {
        const res = await fetch("/api/app/devices/ttlock/access-codes", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            connectionId: lock.connectionId,
            lockId: lock.rawLockId,
            keyboardPwd: codeForm.code,
            keyboardPwdName: codeForm.name,
            ...(codeForm.startsAt ? { startDate: new Date(codeForm.startsAt).getTime() } : {}),
            ...(codeForm.endsAt ? { endDate: new Date(codeForm.endsAt).getTime() } : {}),
          }),
        });
        if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(extractErrMsg(e, "Failed to create access code")); }
        return res.json();
      }
    },
    onSuccess: () => {
      const lock = codeDialogLock ?? selectedLock;
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/lock-access-codes", lock?.id] });
      setShowCodeDialog(false);
      setCodeForm({ name: "", code: "", startsAt: "", endsAt: "" });
      toast({ title: "Access code created" });
    },
    onError: (err: unknown) => {
      toast({ title: extractErrMsg(err, "Failed to create access code"), variant: "destructive" });
    },
  });

  const updateAccessCodeMutation = useMutation({
    mutationFn: async () => {
      const lock = codeDialogLock ?? selectedLock;
      if (!lock || !editingCode) throw new Error("No code selected for edit");
      if (lock.provider === "seam") {
        const res = await fetch(`/api/app/devices/seam/access-codes/${encodeURIComponent(editingCode.id)}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            connectionId: lock.connectionId,
            name: codeForm.name,
            code: codeForm.code || undefined,
            ...(codeForm.startsAt ? { startsAt: codeForm.startsAt } : {}),
            ...(codeForm.endsAt ? { endsAt: codeForm.endsAt } : {}),
          }),
        });
        if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(extractErrMsg(e, "Failed to update access code")); }
        return res.json();
      } else {
        const res = await fetch(`/api/app/devices/ttlock/access-codes/${encodeURIComponent(editingCode.id)}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            connectionId: lock.connectionId,
            lockId: lock.rawLockId,
            newKeyboardPwd: codeForm.code,
            keyboardPwdName: codeForm.name,
            ...(codeForm.startsAt ? { startDate: new Date(codeForm.startsAt).getTime() } : {}),
            ...(codeForm.endsAt ? { endDate: new Date(codeForm.endsAt).getTime() } : {}),
          }),
        });
        if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(extractErrMsg(e, "Failed to update access code")); }
        return res.json();
      }
    },
    onSuccess: () => {
      const lock = codeDialogLock ?? selectedLock;
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/lock-access-codes", lock?.id] });
      setShowCodeDialog(false);
      setEditingCode(null);
      setCodeForm({ name: "", code: "", startsAt: "", endsAt: "" });
      toast({ title: "Access code updated" });
    },
    onError: (err: unknown) => {
      toast({ title: extractErrMsg(err, "Failed to update access code"), variant: "destructive" });
    },
  });

  const setAutoLockMutation = useMutation({
    mutationFn: async ({ enabled, delaySec }: { enabled: boolean; delaySec?: number }) => {
      if (!selectedLock?.connectionId) throw new Error("No lock selected");
      const isSeam = selectedLock.provider === "seam";
      const url = isSeam ? "/api/app/devices/seam/auto-lock" : "/api/app/devices/ttlock/auto-lock";
      const reqBody = isSeam
        ? { connectionId: selectedLock.connectionId, deviceId: selectedLock.id, enabled, delaySec }
        : { connectionId: selectedLock.connectionId, lockId: selectedLock.rawLockId, enabled, delaySec };
      if (!isSeam && !selectedLock.rawLockId) throw new Error("No lock selected");
      const res = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(reqBody),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({})) as { message?: string };
        throw new Error(errBody.message || "Failed to update auto-lock");
      }
      return res.json() as Promise<AutoLockState & { success: boolean }>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/app/devices/auto-lock", selectedLock?.provider, selectedLock?.connectionId, selectedLock?.rawLockId ?? selectedLock?.id],
      });
      toast({ title: "Auto-lock updated" });
    },
    onError: (err: unknown) => {
      toast({ title: extractErrMsg(err, "Failed to update auto-lock"), variant: "destructive" });
    },
  });

  const DRAWER_ACTIVITY_PAGE_SIZE = 30;
  const drawerActivityQuery = useQuery<{ records: TtlockUnlockRecord[]; limit: number; offset: number; lastSyncedAt: string | null }>({
    queryKey: ["/api/app/devices/ttlock/drawer-records", selectedLock?.connectionId, selectedLock?.rawLockId, drawerActivityPage],
    queryFn: async () => {
      if (!selectedLock?.rawLockId || !selectedLock?.connectionId) return { records: [], limit: DRAWER_ACTIVITY_PAGE_SIZE, offset: 0, lastSyncedAt: null };
      const offset = drawerActivityPage * DRAWER_ACTIVITY_PAGE_SIZE;
      const res = await fetch(
        `/api/app/devices/ttlock/locks/${selectedLock.rawLockId}/records?connectionId=${selectedLock.connectionId}&limit=${DRAWER_ACTIVITY_PAGE_SIZE}&offset=${offset}`,
        { credentials: "include" },
      );
      if (!res.ok) return { records: [], limit: DRAWER_ACTIVITY_PAGE_SIZE, offset: 0, lastSyncedAt: null };
      return res.json();
    },
    enabled: !!(open && drawerTab === "activity" && selectedLock?.provider === "ttlock" && selectedLock?.rawLockId != null && selectedLock?.connectionId != null),
    staleTime: 60_000,
  });

  const drawerActivityLastSyncedAt: string | null = drawerActivityQuery.data?.lastSyncedAt ?? null;
  const drawerActivityLastSyncedLabel = formatRelativeSync(drawerActivityLastSyncedAt);

  // Keep the "Last synced" label live while the Activity tab is open.
  const [, setDrawerActivityNowTick] = useState(0);
  useEffect(() => {
    if (!(open && drawerTab === "activity")) return;
    const id = setInterval(() => setDrawerActivityNowTick((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, [open, drawerTab]);

  // Pull fresh unlock records from the provider (not just re-read the DB cache),
  // so the Activity tab isn't stale. Used both by the auto-sync on tab open and
  // the manual Refresh button.
  const drawerRecordsSyncMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async () => {
      if (!selectedLock?.rawLockId || !selectedLock?.connectionId) {
        throw new Error("No lock selected");
      }
      const res = await fetch(
        `/api/app/devices/ttlock/locks/${selectedLock.rawLockId}/records/sync`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ connectionId: selectedLock.connectionId }),
        },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const err = new Error(data?.message || "Failed to refresh records") as Error & { code?: string };
        err.code = data?.code;
        throw err;
      }
      return data as { success: true; inserted: number };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/app/devices/ttlock/drawer-records", selectedLock?.connectionId, selectedLock?.rawLockId],
      });
      setDrawerActivityPage(0);
    },
  });

  // Auto-sync once each time the Activity tab is entered (per lock) so the log
  // reflects fresh provider data without requiring a manual Refresh. The ref is
  // cleared when the tab/drawer closes so re-entering re-syncs.
  const drawerActivityAutoSyncedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const eligible =
      open &&
      drawerTab === "activity" &&
      selectedLock?.provider === "ttlock" &&
      selectedLock?.rawLockId != null &&
      selectedLock?.connectionId != null;
    if (!eligible) {
      drawerActivityAutoSyncedKeyRef.current = null;
      return;
    }
    const key = `${selectedLock!.connectionId}:${selectedLock!.rawLockId}`;
    if (drawerActivityAutoSyncedKeyRef.current === key) return;
    drawerActivityAutoSyncedKeyRef.current = key;
    drawerRecordsSyncMutation.mutate();
  }, [
    open,
    drawerTab,
    selectedLock?.provider,
    selectedLock?.connectionId,
    selectedLock?.rawLockId,
    drawerRecordsSyncMutation.mutate,
  ]);

  const members = membersQuery.data || [];

  return (
    <>
      {/* Lock detail Sheet */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/40"
          onClick={() => {
            if (showCodeDialog || createAccessCodeMutation.isPending || updateAccessCodeMutation.isPending) return;
            onOpenChange(false);
          }}
        />
      )}
      <Sheet
        open={open}
        onOpenChange={(nextOpen) => {
          if (!nextOpen && (showCodeDialog || createAccessCodeMutation.isPending || updateAccessCodeMutation.isPending)) return;
          onOpenChange(nextOpen);
        }}
        modal={false}
      >
        <SheetContent
          className="w-full sm:max-w-[480px] overflow-y-auto p-0 z-50"
          onInteractOutside={(e) => {
            if (showCodeDialog || createAccessCodeMutation.isPending || updateAccessCodeMutation.isPending) e.preventDefault();
          }}
          onPointerDownOutside={(e) => {
            if (showCodeDialog || createAccessCodeMutation.isPending || updateAccessCodeMutation.isPending) e.preventDefault();
          }}
          onEscapeKeyDown={(e) => {
            if (showCodeDialog || createAccessCodeMutation.isPending || updateAccessCodeMutation.isPending) e.preventDefault();
          }}
        >
          {selectedLock && (
            <>
              <SheetHeader className="p-6 pb-4 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div
                    className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      selectedLock.status === "locked" ? "bg-green-50" : "bg-yellow-50"
                    }`}
                  >
                    {selectedLock.status === "locked" ? (
                      <Lock className="h-5 w-5 text-green-600" />
                    ) : (
                      <LockOpen className="h-5 w-5 text-yellow-600" />
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <SheetTitle className="text-lg font-semibold text-gray-900">
                        {selectedLock.name}
                      </SheetTitle>
                      <ProviderBadge provider={selectedLock.provider} />
                      <AccessScopeBadge scope={selectedLock.accessScope} />
                    </div>
                    <p className="text-xs text-gray-500">
                      {selectedLock.brand} {selectedLock.model}
                    </p>
                    {(() => {
                      const isSeam = selectedLock.provider === "seam";
                      const idLabel = isSeam ? "Device ID" : "Lock ID";
                      const idValue = isSeam
                        ? selectedLock.id
                        : selectedLock.rawLockId != null
                          ? String(selectedLock.rawLockId)
                          : null;
                      if (!idValue) return null;
                      return (
                        <div className="flex items-center gap-1 mt-0.5">
                          <span className="text-[10px] text-gray-400">{idLabel}:</span>
                          <span
                            className="text-[10px] font-mono text-gray-500"
                            data-testid="lock-id-value"
                          >
                            {idValue}
                          </span>
                          <button
                            type="button"
                            data-testid="lock-id-copy"
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                            onClick={() => {
                              navigator.clipboard.writeText(idValue).then(() => {
                                toast({ title: "Copied!", duration: 1500 });
                              });
                            }}
                          >
                            <Copy className="h-3 w-3" />
                          </button>
                        </div>
                      );
                    })()}
                  </div>
                </div>

                <div className="flex items-center gap-4 mt-4">
                  <div className="flex items-center gap-1.5">
                    <BatteryIcon level={selectedLock.battery} />
                    <span className="text-xs text-gray-600">{selectedLock.battery}%</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Signal
                      className={`h-3.5 w-3.5 ${selectedLock.online ? "text-green-500" : "text-gray-300"}`}
                    />
                    <span className="text-xs text-gray-500">
                      {selectedLock.online ? "Online" : "Offline"}
                    </span>
                  </div>
                  <Badge
                    variant="outline"
                    className={`text-[10px] ${
                      selectedLock.status === "locked"
                        ? "bg-green-50 text-green-700 border-green-200"
                        : "bg-yellow-50 text-yellow-700 border-yellow-200"
                    }`}
                  >
                    {selectedLock.status === "locked" ? "Locked" : "Unlocked"}
                  </Badge>
                </div>
              </SheetHeader>

              <div className="border-b border-gray-200">
                <div className="flex">
                  {([
                    { key: "access_codes" as DrawerTab, label: "Access Codes" },
                    { key: "manage" as DrawerTab, label: "Manage" },
                    { key: "activity" as DrawerTab, label: "Activity" },
                  ] as const).map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => setDrawerTab(tab.key)}
                      className={`flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                        drawerTab === tab.key
                          ? "border-brand text-brand"
                          : "border-transparent text-gray-500 hover:text-gray-700"
                      }`}
                      data-testid={`tab-drawer-${tab.key}`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="p-6">
                {drawerTab === "access_codes" && (
                  <div className="space-y-6">
                    <div>
                      <div className="flex items-center justify-between mb-3 gap-2">
                        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                          <Key className="h-4 w-4 text-brand" />
                          Programmed Codes
                          {!lockAccessCodesQuery.isLoading && (
                            <span className="text-xs font-normal text-gray-400">({realAccessCodes.length})</span>
                          )}
                        </h3>
                        <div className="flex items-center gap-2">
                          {accessCodesLastSyncedLabel && !lockAccessCodesQuery.isLoading && !lockAccessCodesQuery.isError && (
                            <span
                              className="text-xs text-gray-500"
                              title={accessCodesLastSyncedAt ? new Date(accessCodesLastSyncedAt).toLocaleString() : undefined}
                              data-testid="text-access-codes-last-synced"
                            >
                              Last updated {accessCodesLastSyncedLabel}
                            </span>
                          )}
                          {!readOnly && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs"
                              onClick={() => {
                                setEditingCode(null);
                                setCodeForm({ name: "", code: "", startsAt: "", endsAt: "" });
                                setCodeDialogLock(selectedLock);
                                setShowCodeDialog(true);
                              }}
                              data-testid="button-new-access-code"
                            >
                              <Plus className="h-3 w-3 mr-1" />
                              New Code
                            </Button>
                          )}
                        </div>
                      </div>
                      {lockAccessCodesQuery.isLoading ? (
                        <div className="text-center py-6 bg-gray-50 rounded-lg">
                          <p className="text-xs text-gray-400">Loading access codes…</p>
                        </div>
                      ) : lockAccessCodesQuery.isError ? (() => {
                        const err = lockAccessCodesQuery.error as (Error & { status?: number }) | null;
                        const isTtlockExpired =
                          selectedLock.provider === "ttlock" &&
                          (err?.status === 422 || /TTLock connection is expired/i.test(err?.message || ""));
                        if (isTtlockExpired) {
                          return (
                            <div
                              className="rounded-lg border border-amber-200 bg-amber-50 p-4 flex items-start gap-3"
                              data-testid="callout-ttlock-connection-expired"
                            >
                              <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-amber-900">
                                  TTLock connection expired
                                </p>
                                <p className="text-xs text-amber-800 mt-1">
                                  Reconnect your TTLock account to view and manage access codes for this lock.
                                </p>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="mt-3 border-amber-300 text-amber-800 hover:bg-amber-100 text-xs h-7"
                                  onClick={() => {
                                    onOpenChange(false);
                                    navigateLocation("/app/devices/smart-locks?tab=connected_accounts");
                                  }}
                                  data-testid="button-reconnect-ttlock-from-access-codes"
                                >
                                  <RefreshCw className="h-3 w-3 mr-1" />
                                  Reconnect TTLock
                                </Button>
                              </div>
                            </div>
                          );
                        }
                        return (
                          <div className="text-center py-6 bg-red-50 rounded-lg">
                            <p className="text-xs text-red-500">
                              {err?.message || "Failed to load access codes"}
                            </p>
                          </div>
                        );
                      })() : realAccessCodes.length === 0 ? (
                        <div className="text-center py-6 bg-gray-50 rounded-lg">
                          <Key className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                          <p className="text-xs text-gray-400">No access codes programmed on this lock</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {realAccessCodes.map((code) => (
                            <AccessCodeCard
                              key={code.id}
                              code={code}
                              onEdit={() => {
                                setEditingCode(code);
                                setCodeForm({ name: code.name, code: code.code, startsAt: "", endsAt: "" });
                                setCodeDialogLock(selectedLock);
                                setShowCodeDialog(true);
                              }}
                              onRevoke={() => revokeAccessCodeMutation.mutate({ codeId: code.id })}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {drawerTab === "manage" && (
                  <div className="space-y-6">
                    {(() => {
                      const currentAssignment = (assignmentsQuery.data?.assignments || []).find(
                        (a) => a.deviceId === selectedLock.id
                      );
                      const propertyMembers = members.filter(
                        (m) => m.status === "active" && (!effectivePropertyId || m.currentPropertyId === effectivePropertyId)
                      );
                      const serverDefaultMemberId = assignmentsQuery.data?.defaults[selectedLock.id] ?? null;
                      const effectiveValue = currentAssignment
                        ? String(currentAssignment.memberId)
                        : serverDefaultMemberId
                          ? String(serverDefaultMemberId)
                          : "none";
                      return (
                        <div data-testid="section-member-assignment">
                          <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                            <User className="h-4 w-4 text-brand" />
                            Battery Alert Recipient
                          </h3>
                          <p className="text-xs text-gray-500 mb-3">
                            Assign a specific member to receive low-battery alerts for this lock. If no member is assigned, all active members at the property will be notified.
                          </p>
                          <div className="flex items-center gap-2">
                            <Select
                              value={effectiveValue}
                              onValueChange={(value) => {
                                if (value === "none") {
                                  if (currentAssignment) {
                                    clearAssignmentMutation.mutate(selectedLock.id);
                                  }
                                } else {
                                  setAssignmentMutation.mutate({
                                    deviceId: selectedLock.id,
                                    memberId: parseInt(value, 10),
                                  });
                                }
                              }}
                            >
                              <SelectTrigger className="flex-1" data-testid="select-assigned-member">
                                <SelectValue placeholder="No member assigned (notify all)" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none" data-testid="option-no-member">
                                  No member assigned (notify all)
                                </SelectItem>
                                {propertyMembers.map((m) => (
                                  <SelectItem key={m.id} value={String(m.id)} data-testid={`option-member-${m.id}`}>
                                    {m.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          {!currentAssignment && serverDefaultMemberId && (
                            <p className="text-xs text-blue-500 mt-2" data-testid="text-auto-recipient">
                              Auto-set from room assignment — override by selecting a different member above.
                            </p>
                          )}
                          {propertyMembers.length === 0 && (
                            <p className="text-xs text-gray-400 mt-2">No active members in this property yet.</p>
                          )}
                        </div>
                      );
                    })()}

                    <Separator />

                    {(() => {
                      const coversLowBattery = (c: string) => c === "low_battery" || c === "all";
                      const allMutes = mutesQuery.data || [];
                      const deviceMute = allMutes.find(
                        (m) => m.deviceId === selectedLock.id && coversLowBattery(m.condition),
                      );
                      const propertyMuteCovering = effectivePropertyId
                        ? allMutes.find(
                            (m) => m.propertyId === effectivePropertyId && coversLowBattery(m.condition),
                          )
                        : undefined;
                      const isMuted = !!deviceMute || !!propertyMuteCovering;
                      const expiresAt = deviceMute?.expiresAt
                        ? new Date(deviceMute.expiresAt)
                        : propertyMuteCovering?.expiresAt
                          ? new Date(propertyMuteCovering.expiresAt)
                          : null;
                      return (
                        <div data-testid="section-mute-alerts">
                          <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                            <BellOff className="h-4 w-4 text-brand" />
                            Mute Low-Battery Alerts
                          </h3>
                          <p className="text-xs text-gray-500 mb-3">
                            Silence the recurring 12-hour low-battery reminder for this lock without losing visibility on other locks at the property.
                          </p>
                          {propertyMuteCovering && !deviceMute && (
                            <p className="text-xs text-amber-600 mb-3" data-testid="text-property-mute-active">
                              This lock is silenced by a property-wide mute. Unmute the property to receive alerts again.
                            </p>
                          )}
                          {isMuted ? (
                            <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                              <div className="flex items-center gap-2">
                                <BellOff className="h-4 w-4 text-gray-500" />
                                <div>
                                  <p className="text-sm text-gray-900" data-testid="text-mute-status">
                                    Muted{expiresAt ? ` until ${expiresAt.toLocaleString()}` : " until you unmute"}
                                  </p>
                                </div>
                              </div>
                              {deviceMute && !readOnly && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => deleteMuteMutation.mutate(deviceMute.id)}
                                  disabled={deleteMuteMutation.isPending}
                                  data-testid="button-unmute-device"
                                >
                                  <Bell className="h-3.5 w-3.5 mr-1" />
                                  Unmute
                                </Button>
                              )}
                            </div>
                          ) : (
                            <div className="flex flex-wrap gap-2">
                              {[
                                { hours: 24 as const, label: "24 hours" },
                                { hours: 168 as const, label: "7 days" },
                                { hours: 0 as const, label: "Until I unmute" },
                              ].map((opt) => (
                                <Button
                                  key={opt.hours}
                                  size="sm"
                                  variant="outline"
                                  disabled={readOnly || createMuteMutation.isPending}
                                  onClick={() => createMuteMutation.mutate({ deviceId: selectedLock.id, durationHours: opt.hours })}
                                  data-testid={`button-mute-device-${opt.hours}`}
                                >
                                  <BellOff className="h-3.5 w-3.5 mr-1" />
                                  {opt.label}
                                </Button>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    <Separator />

                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 mb-4">Remote Control</h3>
                      <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                        <div className="flex items-center gap-2">
                          {selectedLock.status === "locked" ? (
                            <Lock className="h-4 w-4 text-green-600" />
                          ) : (
                            <LockOpen className="h-4 w-4 text-yellow-600" />
                          )}
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {selectedLock.status === "locked" ? "Currently Locked" : "Currently Unlocked"}
                            </p>
                            <p className="text-xs text-gray-500">
                              {selectedLock.online ? "Lock is online" : "Lock appears offline"}
                            </p>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={readOnly || lockControlMutation.isPending || !selectedLock.online}
                          onClick={() =>
                            lockControlMutation.mutate({
                              deviceId: selectedLock.id,
                              action: selectedLock.status === "locked" ? "unlock" : "lock",
                              connectionId: selectedLock.connectionId,
                            })
                          }
                          data-testid="button-lock-control"
                        >
                          {lockControlMutation.isPending
                            ? "Sending..."
                            : selectedLock.status === "locked" ? "Unlock" : "Lock"}
                        </Button>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 mb-4">Auto-Lock</h3>
                      {(() => {
                        const isTtlock = selectedLock.provider === "ttlock";
                        const isLoading = autoLockQuery.isLoading;
                        const autoLockState = autoLockQuery.data;
                        const supported = autoLockState?.supported ?? null;
                        const enabled = autoLockState?.enabled ?? false;
                        const delaySec = autoLockState?.delaySec ?? 60;
                        const isPending = setAutoLockMutation.isPending;

                        if (isLoading) {
                          return (
                            <div className="bg-gray-50 rounded-lg p-4">
                              <p className="text-xs text-gray-400">Loading auto-lock state…</p>
                            </div>
                          );
                        }

                        if (autoLockQuery.isError) {
                          return (
                            <div className="bg-red-50 rounded-lg p-4">
                              <p className="text-xs text-red-500" data-testid="text-autolock-error">
                                {(autoLockQuery.error as Error)?.message || "Failed to load auto-lock state"}
                              </p>
                            </div>
                          );
                        }

                        if (supported === false && autoLockState != null) {
                          return (
                            <div className="bg-gray-50 rounded-lg p-4">
                              <p className="text-xs text-gray-400" data-testid="text-autolock-unsupported">
                                {isTtlock
                                  ? "This lock model does not support remote auto-lock configuration. Use the TTLock app to enable auto-lock directly on the device."
                                  : "This lock does not expose remote auto-lock configuration. Use your lock's companion app to configure auto-lock."}
                              </p>
                            </div>
                          );
                        }

                        return (
                          <div className="space-y-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <Shield className="h-4 w-4 text-brand" />
                                <div>
                                  <p className="text-sm font-medium text-gray-900">Enable Auto-Lock</p>
                                  <p className="text-xs text-gray-500">Automatically lock after a set time</p>
                                </div>
                              </div>
                              <Switch
                                checked={enabled}
                                disabled={isPending || readOnly}
                                onCheckedChange={(checked) =>
                                  setAutoLockMutation.mutate({ enabled: checked, delaySec: delaySec || 60 })
                                }
                                data-testid="switch-auto-lock"
                              />
                            </div>

                            {enabled && (
                              <div className="ml-7 space-y-2">
                                <p className="text-xs text-gray-500">Auto-lock delay</p>
                                <div className="flex gap-2">
                                  {[
                                    { value: 30, label: "30s" },
                                    { value: 60, label: "1m" },
                                    { value: 120, label: "2m" },
                                    { value: 300, label: "5m" },
                                  ].map((option) => (
                                    <button
                                      key={option.value}
                                      disabled={isPending || readOnly}
                                      className={`px-3 py-1.5 text-xs rounded-md border transition-colors disabled:opacity-50 ${
                                        delaySec === option.value
                                          ? "bg-brand text-white border-brand"
                                          : "bg-white text-gray-600 border-gray-200 hover:border-brand/30"
                                      }`}
                                      onClick={() =>
                                        setAutoLockMutation.mutate({ enabled: true, delaySec: option.value })
                                      }
                                      data-testid={`button-autolock-${option.value}`}
                                    >
                                      {option.label}
                                    </button>
                                  ))}
                                </div>
                                {isPending && (
                                  <p className="text-xs text-gray-400">Saving…</p>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>

                    <Separator />

                    {selectedLock.provider === "seam" && (
                      <div data-testid="section-seam-ignore">
                        <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                          <Ban className="h-4 w-4 text-brand" />
                          Exclude from PadSplit
                        </h3>
                        <p className="text-xs text-gray-500 mb-3">
                          Mark this lock as non-PadSplit (e.g. a personal door or garage). It will be hidden from all
                          property lists, battery alerts, and member access flows.
                        </p>
                        <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                          <div className="flex items-center gap-2">
                            <Ban className="h-4 w-4 text-gray-500" />
                            <p className="text-sm text-gray-900">
                              {selectedLock.ignored ? "Excluded (ignored)" : "Active — used for PadSplit"}
                            </p>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={readOnly || seamIgnoreMutation.isPending}
                            onClick={() =>
                              seamIgnoreMutation.mutate({
                                deviceId: selectedLock.id,
                                ignored: !selectedLock.ignored,
                              })
                            }
                            data-testid="button-seam-toggle-ignore"
                          >
                            {seamIgnoreMutation.isPending
                              ? "Saving…"
                              : selectedLock.ignored
                                ? "Restore"
                                : "Exclude"}
                          </Button>
                        </div>
                      </div>
                    )}

                    <Separator />

                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 mb-4">Device Info</h3>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-500">Brand</span>
                          <span className="text-sm text-gray-900">{selectedLock.brand}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-500">Model</span>
                          <span className="text-sm text-gray-900">{selectedLock.model}</span>
                        </div>
                        {selectedLock.provider && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-500">Provider</span>
                            <ProviderBadge provider={selectedLock.provider} />
                          </div>
                        )}
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-500">Device ID</span>
                          <span className="text-xs font-mono text-gray-500 truncate max-w-[200px]">
                            {selectedLock.id}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {drawerTab === "activity" && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                        <History className="h-4 w-4 text-brand" />
                        Activity Log
                      </h3>
                      {selectedLock.provider === "ttlock" && selectedLock.rawLockId && (
                        <div className="flex items-center gap-2">
                          {drawerActivityLastSyncedLabel && (
                            <span
                              className="text-xs text-gray-500"
                              title={drawerActivityLastSyncedAt ? new Date(drawerActivityLastSyncedAt).toLocaleString() : undefined}
                              data-testid="text-drawer-records-last-synced"
                            >
                              Last synced {drawerActivityLastSyncedLabel}
                            </span>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-xs text-gray-500 h-7"
                            disabled={drawerRecordsSyncMutation.isPending}
                            onClick={() => {
                              drawerRecordsSyncMutation.mutate(undefined, {
                                onSuccess: (data) => {
                                  toast({
                                    title: data.inserted > 0
                                      ? `Found ${data.inserted} new record${data.inserted === 1 ? "" : "s"}`
                                      : "Already up to date",
                                  });
                                },
                                onError: (err: Error & { code?: string }) => {
                                  toast({
                                    title: err.code === "RATE_LIMITED" ? "Slow down" : "Refresh failed",
                                    description: parseErrorMessage(err, "Couldn't refresh lock records. Please try again."),
                                    variant: "destructive",
                                  });
                                },
                              });
                            }}
                            data-testid="button-drawer-activity-sync"
                          >
                            <RefreshCw className={`h-3 w-3 mr-1 ${drawerRecordsSyncMutation.isPending ? "animate-spin" : ""}`} />
                            Refresh
                          </Button>
                        </div>
                      )}
                    </div>

                    {selectedLock.provider === "seam" ? (
                      <div className="bg-gray-50 rounded-lg p-6 text-center">
                        <History className="h-6 w-6 text-gray-300 mx-auto mb-2" />
                        <p className="text-sm text-gray-500">Activity logs are not available for other-brand locks.</p>
                      </div>
                    ) : !selectedLock.rawLockId ? (
                      <div className="bg-gray-50 rounded-lg p-6 text-center">
                        <WifiOff className="h-6 w-6 text-gray-300 mx-auto mb-2" />
                        <p className="text-sm text-gray-500">Activity requires a gateway connection.</p>
                      </div>
                    ) : drawerActivityQuery.isLoading ? (
                      <div className="space-y-2">
                        {[1, 2, 3, 4, 5].map((i) => (
                          <div key={i} className="bg-gray-50 rounded-lg p-3 animate-pulse h-14" />
                        ))}
                      </div>
                    ) : drawerActivityQuery.isError ? (
                      <div className="bg-red-50 rounded-lg p-4 text-center">
                        <p className="text-sm text-red-500">Failed to load activity. Try refreshing.</p>
                      </div>
                    ) : (drawerActivityQuery.data?.records ?? []).length === 0 ? (
                      <div className="bg-gray-50 rounded-lg p-6 text-center">
                        <Clock className="h-6 w-6 text-gray-300 mx-auto mb-2" />
                        <p className="text-sm text-gray-500">No activity records found.</p>
                        {drawerActivityPage > 0 && (
                          <Button size="sm" variant="ghost" className="mt-2 text-xs" onClick={() => setDrawerActivityPage(0)}>
                            Back to start
                          </Button>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {(drawerActivityQuery.data?.records ?? []).map((rec, i) => {
                          const ts = rec.recordTime ? new Date(rec.recordTime) : null;
                          const label = rec.recordTypeLabel || "Event";
                          return (
                            <div key={i} className="flex items-start gap-3 bg-gray-50 rounded-lg p-3">
                              <div className="w-7 h-7 rounded-full bg-white border border-gray-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                                <Clock className="h-3.5 w-3.5 text-gray-400" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900">{label}</p>
                                {rec.username && (
                                  <p className="text-xs text-gray-500">{rec.username}</p>
                                )}
                                {ts && (
                                  <p className="text-xs text-gray-400 mt-0.5">
                                    {ts.toLocaleDateString()} {ts.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                                  </p>
                                )}
                              </div>
                            </div>
                          );
                        })}
                        <div className="flex items-center justify-between pt-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs h-7"
                            disabled={drawerActivityPage === 0}
                            onClick={() => setDrawerActivityPage((p) => Math.max(0, p - 1))}
                            data-testid="button-activity-newer"
                          >
                            Newer
                          </Button>
                          <span className="text-xs text-gray-400">Page {drawerActivityPage + 1}</span>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs h-7"
                            disabled={(drawerActivityQuery.data?.records ?? []).length < DRAWER_ACTIVITY_PAGE_SIZE}
                            onClick={() => setDrawerActivityPage((p) => p + 1)}
                            data-testid="button-activity-older"
                          >
                            Older
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Access code dialog */}
      <Dialog open={showCodeDialog} onOpenChange={(nextOpen) => {
        if (!nextOpen && (createAccessCodeMutation.isPending || updateAccessCodeMutation.isPending)) return;
        setShowCodeDialog(nextOpen);
        if (!nextOpen) { setEditingCode(null); setCodeForm({ name: "", code: "", startsAt: "", endsAt: "" }); setCodeDialogLock(null); }
      }}>
        <DialogContent
          className="sm:max-w-md"
          onInteractOutside={(e) => {
            if (createAccessCodeMutation.isPending || updateAccessCodeMutation.isPending) e.preventDefault();
          }}
          onEscapeKeyDown={(e) => {
            if (createAccessCodeMutation.isPending || updateAccessCodeMutation.isPending) e.preventDefault();
          }}
          onPointerDownOutside={(e) => {
            if (createAccessCodeMutation.isPending || updateAccessCodeMutation.isPending) e.preventDefault();
          }}
        >
          <DialogHeader>
            <DialogTitle>{editingCode ? "Edit Access Code" : "New Access Code"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label htmlFor="sl-code-name">Name</Label>
              <Input
                id="sl-code-name"
                placeholder="e.g. Front Door Code"
                value={codeForm.name}
                onChange={(e) => setCodeForm((f) => ({ ...f, name: e.target.value }))}
                data-testid="input-code-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sl-code-pin">PIN Code</Label>
              <Input
                id="sl-code-pin"
                placeholder={editingCode ? "Enter new PIN to change" : "e.g. 1234"}
                value={codeForm.code}
                onChange={(e) => setCodeForm((f) => ({ ...f, code: e.target.value }))}
                data-testid="input-code-pin"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="sl-code-starts">Start (optional)</Label>
                <Input
                  id="sl-code-starts"
                  type="datetime-local"
                  value={codeForm.startsAt}
                  onChange={(e) => setCodeForm((f) => ({ ...f, startsAt: e.target.value }))}
                  data-testid="input-code-starts"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sl-code-ends">End (optional)</Label>
                <Input
                  id="sl-code-ends"
                  type="datetime-local"
                  value={codeForm.endsAt}
                  onChange={(e) => setCodeForm((f) => ({ ...f, endsAt: e.target.value }))}
                  data-testid="input-code-ends"
                />
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowCodeDialog(false)}>
                Cancel
              </Button>
              <Button
                className="flex-1 bg-brand hover:bg-brand-hover text-white"
                disabled={
                  !codeForm.name ||
                  (!editingCode && !codeForm.code) ||
                  createAccessCodeMutation.isPending ||
                  updateAccessCodeMutation.isPending
                }
                onClick={() => {
                  if (editingCode) {
                    updateAccessCodeMutation.mutate();
                  } else {
                    createAccessCodeMutation.mutate();
                  }
                }}
                data-testid="button-code-submit"
              >
                {(createAccessCodeMutation.isPending || updateAccessCodeMutation.isPending)
                  ? "Saving..."
                  : editingCode ? "Save Changes" : "Create Code"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─── SmartLocksPropertyBrowser ────────────────────────────────────────────────

export function SmartLocksPropertyBrowser() {
  const queryClient = useQueryClient();
  const { canEdit: __canEditArea } = usePermissions();
  const readOnly = !__canEditArea("devices");
  const { toast } = useToast();
  const [, navigateLocation] = useLocation();

  const [viewMode, setViewMode] = useState<ViewMode>("properties");
  const [selectedPropertyId, setSelectedPropertyId] = useState<number | null>(null);
  const [selectedLock, setSelectedLock] = useState<UnifiedLock | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [propertySort, setPropertySort] = useListState<"name" | "address" | "rooms" | "locks">("smart-locks:sort", "name");
  const [propertySortDir, setPropertySortDir] = useListState<"asc" | "desc">("smart-locks:direction", "asc");
  const [searchQuery, setSearchQuery] = useListState("smart-locks:search", "");
  const [lockSearchQuery, setLockSearchQuery] = useListState("smart-locks:lock-search", "");
  const [scopeFilter, setScopeFilter] = useListState<Set<"common" | "room" | "admin">>("smart-locks:scope", new Set());
  const [gatewayDisconnectedOnly, setGatewayDisconnectedOnly] = useState(false);
  const [lowBatteryOnly, setLowBatteryOnly] = useState(false);

  // Deep-link support: ?deviceId=<id> or ?propertyId=<id> in the parent URL
  // (e.g. forwarded from /app/devices/smart-locks?deviceId=… notification links)
  const rawSearch = useSearch();
  const deepLinkDeviceId = useMemo(() => new URLSearchParams(rawSearch).get("deviceId"), [rawSearch]);
  const deepLinkPropertyId = useMemo(() => {
    const p = new URLSearchParams(rawSearch).get("propertyId");
    return p ? parseInt(p, 10) : null;
  }, [rawSearch]);

  // Find which property owns the deep-link device
  const findPropertyQuery = useQuery<{ propertyId: number | null }>({
    queryKey: ["/api/app/devices/locks/find-property", deepLinkDeviceId],
    queryFn: async () => {
      if (!deepLinkDeviceId) return { propertyId: null };
      const res = await fetch(
        `/api/app/devices/locks/find-property?deviceId=${encodeURIComponent(deepLinkDeviceId)}`,
        { credentials: "include" }
      );
      if (!res.ok) return { propertyId: null };
      return res.json();
    },
    enabled: !!deepLinkDeviceId,
  });

  // Phase 1: auto-navigate to the owning property
  const deepLinkPropertyNavigatedRef = useRef(false);
  useEffect(() => {
    if (deepLinkPropertyNavigatedRef.current) return;
    const propId = deepLinkPropertyId ?? findPropertyQuery.data?.propertyId ?? null;
    if (!propId) return;
    deepLinkPropertyNavigatedRef.current = true;
    setSelectedPropertyId(propId);
    setViewMode("locks");
  }, [deepLinkPropertyId, findPropertyQuery.data]);

  // Phase 2: once locks for the property load, open the target lock drawer
  const deepLinkLockOpenedRef = useRef(false);

  const propertiesQuery = useQuery<Property[]>({
    queryKey: ["/api/app/properties"],
  });

  const lockCountsQuery = useQuery<Record<number, number>>({
    queryKey: ["/api/app/devices/locks/counts-by-property"],
    staleTime: 60_000,
  });

  const roomsQuery = useQuery<Room[]>({
    queryKey: ["/api/app/properties", selectedPropertyId, "rooms"],
    queryFn: async () => {
      if (!selectedPropertyId) return [];
      const res = await fetch(`/api/app/properties/${selectedPropertyId}/rooms`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch rooms");
      return res.json();
    },
    enabled: !!selectedPropertyId,
  });

  const mutesQuery = useQuery<LockHealthMute[]>({
    queryKey: ["/api/app/devices/lock-health-mutes"],
  });

  const allLocksQuery = useQuery<ProviderLock[]>({
    queryKey: ["/api/app/devices/locks/list-for-property", selectedPropertyId],
    queryFn: async () => {
      if (!selectedPropertyId) return [];
      const res = await fetch(
        `/api/app/devices/locks/list-for-property?propertyId=${selectedPropertyId}`,
        { credentials: "include" },
      );
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!selectedPropertyId,
    staleTime: 60_000,
  });

  // Deep-link support: ?deviceId= navigates to the owning property and
  // highlights the lock; ?propertyId= auto-selects a property's lock list.
  const highlightedDeviceId = new URLSearchParams(window.location.search).get("deviceId") ?? undefined;
  const requestedPropertyId = (() => {
    const v = new URLSearchParams(window.location.search).get("propertyId");
    return v != null ? parseInt(v, 10) : null;
  })();
  const handledNavigationRef = useRef<string | null>(null);
  const handledPropertyParamRef = useRef<number | null>(null);
  const handledHighlightRef = useRef<string | null>(null);
  const lockCardRefs = useRef<Record<string, HTMLElement | null>>({});
  const [flashingDeviceId, setFlashingDeviceId] = useState<string | null>(null);

  const seamLocksQuery = { data: allLocksQuery.data?.filter((l) => l.provider === "seam") as SeamPropertyLock[] | undefined, isLoading: allLocksQuery.isLoading };
  const ttlockLocksQuery = { data: allLocksQuery.data?.filter((l) => l.provider === "ttlock") as TtlockPropertyLock[] | undefined, isLoading: allLocksQuery.isLoading };

  // Task #941 — Mute / unmute lock-health alerts at device or property scope.
  // Per-device mutes scope to "low_battery" (the only alert exposed in the
  // drawer today); property-wide mutes silence every condition ("all").
  const createMuteMutation = useMutation({
    mutationFn: async (input: { deviceId?: string; propertyId?: number; durationHours: 0 | 24 | 168 }) => {
      return apiRequest("POST", "/api/app/devices/lock-health-mutes", {
        deviceId: input.deviceId ?? null,
        propertyId: input.propertyId ?? null,
        condition: input.propertyId ? "all" : "low_battery",
        durationHours: input.durationHours,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/lock-health-mutes"] });
      toast({ title: "Alerts muted", description: "Low-battery notifications will be silenced for the chosen window." });
    },
    onError: (err: unknown) => {
      toast({ title: extractErrMsg(err, "Failed to mute alerts"), variant: "destructive" });
    },
  });

  const deleteMuteMutation = useMutation({
    mutationFn: async (id: number) => {
      return apiRequest("DELETE", `/api/app/devices/lock-health-mutes/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/lock-health-mutes"] });
      toast({ title: "Alerts unmuted", description: "You'll start receiving low-battery notifications again." });
    },
    onError: () => {
      toast({ title: "Failed to unmute alerts", variant: "destructive" });
    },
  });

  const activeProperties = sortProperties((propertiesQuery.data || []).filter(
    (p) => p.status !== "archive"
  ));

  const filteredProperties = searchQuery
    ? activeProperties.filter(
        (p) =>
          String(p.name ?? "").toLowerCase().includes(searchQuery.toLowerCase()) ||
          String(p.address ?? "").toLowerCase().includes(searchQuery.toLowerCase())
      )
    : activeProperties;

  const sortedProperties = [...filteredProperties].sort((a, b) => {
    const dir = propertySortDir === "asc" ? 1 : -1;
    if (propertySort === "name") return a.name.localeCompare(b.name) * dir;
    if (propertySort === "address") return (a.address || "").localeCompare(b.address || "") * dir;
    if (propertySort === "rooms") return ((a.roomCount ?? 0) - (b.roomCount ?? 0)) * dir;
    if (propertySort === "locks") {
      const lockA = lockCountsQuery.data?.[a.id] ?? 0;
      const lockB = lockCountsQuery.data?.[b.id] ?? 0;
      return (lockA - lockB) * dir;
    }
    return 0;
  });

  const selectedProperty = activeProperties.find((p) => p.id === selectedPropertyId);

  const allRooms = roomsQuery.data || [];
  const rooms = allRooms.filter(
    (r) => r.roomType !== "bathroom" && !r.roomNumber?.toLowerCase().startsWith("bath")
  );

  const realSeamLocks = seamLocksQuery.data || [];
  const realTtlockLocks = ttlockLocksQuery.data || [];

  const locks: UnifiedLock[] = [
    ...realSeamLocks.map((l, i): UnifiedLock => ({
      id: l.deviceId,
      name: l.displayName || l.locationName || `Smart Lock ${i + 1}`,
      roomId: l.roomId ?? 0,
      roomNumber: l.locationName || "",
      propertyId: l.propertyId ?? null,
      brand: "Other",
      model: "Smart Lock",
      status: l.locked === false ? "unlocked" : "locked",
      battery: l.batteryPct ?? 0,
      online: l.online,
      lastActivity: "",
      autoLock: null,
      autoLockDelay: null,
      isReal: true,
      provider: "seam",
      connectionId: l.connectionId,
      accessScope: l.accessScope ?? "common",
      hasGateway: l.hasGateway,
    })),
    ...realTtlockLocks.map((l, i): UnifiedLock => {
      const parts = l.deviceId.split("-");
      const rawLockId = parseInt(parts[parts.length - 1], 10);
      let assignedLabel: string | null = null;
      if (l.roomId != null) {
        const assignedRoom = allRooms.find((r) => r.id === l.roomId);
        if (assignedRoom) {
          assignedLabel = assignedRoom.roomNumber || `Room ${assignedRoom.id}`;
        }
      } else if (l.isCommonSpace) {
        assignedLabel = "Common space";
      }
      // Task #1663 — promote common-scope locks that are pinned to a
      // specific room to the new "room" scope so the LockCard renders
      // the light-blue Room badge. Admin scope is never reclassified.
      const rawScope = l.accessScope ?? "common";
      const scope: "common" | "admin" | "room" =
        rawScope === "admin" ? "admin" : l.roomId != null ? "room" : "common";
      return {
        id: l.deviceId,
        name: l.displayName || l.locationName || `TTLock ${i + 1}`,
        roomId: l.roomId ?? 0,
        roomNumber: l.locationName || "",
        propertyId: l.propertyId ?? null,
        brand: "TTLock",
        model: "Smart Lock",
        status: l.locked === false ? "unlocked" : "locked",
        battery: l.batteryPct ?? 0,
        online: l.online,
        lastActivity: "",
        autoLock: null,
        autoLockDelay: null,
        isReal: true,
        provider: "ttlock",
        connectionId: l.connectionId,
        rawLockId: isNaN(rawLockId) ? undefined : rawLockId,
        assignedLabel,
        accessScope: scope,
        hasGateway: l.hasGateway,
      };
    }),
  ];

  // Task #1663 — compose the existing search with the new scope /
  // gateway-disconnected / low-battery filters so they all stack.
  const hasActiveFilters = scopeFilter.size > 0 || gatewayDisconnectedOnly || lowBatteryOnly;
  const filteredLocks = locks.filter((l) => {
    if (lockSearchQuery) {
      const q = lockSearchQuery.toLowerCase();
      if (!String(l.name ?? "").toLowerCase().includes(q) && !String(l.brand ?? "").toLowerCase().includes(q)) return false;
    }
    if (scopeFilter.size > 0) {
      const scope = l.accessScope ?? "common";
      if (!scopeFilter.has(scope as "common" | "room" | "admin")) return false;
    }
    if (gatewayDisconnectedOnly && l.hasGateway !== false) return false;
    if (lowBatteryOnly && !(l.battery < 20)) return false;
    return true;
  });
  const clearAllFilters = () => {
    setScopeFilter(new Set());
    setGatewayDisconnectedOnly(false);
    setLowBatteryOnly(false);
  };

  const handlePropertyClick = (propertyId: number) => {
    setSelectedPropertyId(propertyId);
    setViewMode("locks");
    setLockSearchQuery("");
  };

  const handleLockClick = (lock: UnifiedLock) => {
    setSelectedLock(lock);
    setDrawerOpen(true);
  };

  const handleBackToProperties = () => {
    setViewMode("properties");
    setSelectedPropertyId(null);
    setSelectedLock(null);
    setDrawerOpen(false);
  };

  useEffect(() => {
    if (!drawerOpen) {
      setSelectedLock(null);
    }
  }, [drawerOpen]);

  // Resolve `?deviceId=<id>` to its owning property so we can switch to the
  // locks list for the right property before highlighting. Falls back to a
  // null result when no property contains the lock; the highlight effect
  // below treats that as a graceful no-op.
  const findPropertyForDeviceQuery = useQuery<{ propertyId: number | null }>({
    queryKey: ["/api/app/devices/locks/find-property", highlightedDeviceId],
    queryFn: async () => {
      const res = await fetch(
        `/api/app/devices/locks/find-property?deviceId=${encodeURIComponent(highlightedDeviceId!)}`,
        { credentials: "include" },
      );
      if (!res.ok) return { propertyId: null };
      return res.json();
    },
    enabled: !!highlightedDeviceId,
    staleTime: 60_000,
  });

  // Once we know the property, switch into its locks view. Only fires once
  // per deviceId — after the initial navigation the user can move freely
  // (e.g. back to the properties grid) without being snapped back here.
  useEffect(() => {
    if (!highlightedDeviceId) return;
    if (handledNavigationRef.current === highlightedDeviceId) return;
    const target = findPropertyForDeviceQuery.data?.propertyId;
    if (target == null) return;
    handledNavigationRef.current = highlightedDeviceId;
    if (selectedPropertyId === target && viewMode === "locks") return;
    setSelectedPropertyId(target);
    setViewMode("locks");
    setLockSearchQuery("");
  }, [highlightedDeviceId, findPropertyForDeviceQuery.data, selectedPropertyId, viewMode]);

  // Deep-link support: `/app/devices/smart-locks?propertyId=<id>` (e.g. from
  // the Messages member-info sidebar) should auto-select that property and
  // open its lock list. Fires once per propertyId so the user can navigate
  // away freely without being snapped back.
  useEffect(() => {
    if (requestedPropertyId == null || isNaN(requestedPropertyId)) return;
    if (handledPropertyParamRef.current === requestedPropertyId) return;
    handledPropertyParamRef.current = requestedPropertyId;
    setSelectedPropertyId(requestedPropertyId);
    setViewMode("locks");
    setLockSearchQuery("");
  }, [requestedPropertyId]);

  // Once the lock card is mounted, scroll it into view and flash a ring so
  // the alerting lock is immediately obvious. Falls back gracefully if the
  // deviceId is not in the loaded list (no scroll, no flash).
  //
  // Task #955 — We must wait for `allLocksQuery` for the resolved property to
  // actually settle (success AND not in-flight) before marking this deviceId
  // handled. `isLoading` only covers the very first fetch with no cached
  // data, so a slow Seam list-for-property call (or a stale-cached empty
  // result that's being refetched) could otherwise have us mark the deviceId
  // as handled while the right answer was still arriving — silently eating
  // the flash for the rest of the session.
  useEffect(() => {
    if (!highlightedDeviceId) return;
    if (handledHighlightRef.current === highlightedDeviceId) return;
    if (viewMode !== "locks") return;
    if (!findPropertyForDeviceQuery.isFetched) return;
    // Only proceed once we know which property the deviceId resolved to and
    // that it matches the property whose lock list we're inspecting. Without
    // this we could mark the deviceId handled against a different property's
    // list (e.g. if the user manually flipped properties before resolution).
    const resolvedPropertyId = findPropertyForDeviceQuery.data?.propertyId ?? null;
    if (resolvedPropertyId == null) return;
    if (selectedPropertyId !== resolvedPropertyId) return;
    // Require an actually-completed successful fetch for this property's lock
    // list, not just `!isLoading`. `isLoading` is false whenever there is any
    // cached data (even stale) or the query hasn't started yet, which is
    // exactly when an in-flight slow fetch could be mistaken for "done".
    if (!allLocksQuery.isSuccess) return;
    if (allLocksQuery.isFetching) return;
    const locks = allLocksQuery.data ?? [];
    if (!locks.some((l) => l.deviceId === highlightedDeviceId)) {
      // The lock list for this property has settled and the deviceId truly
      // isn't in it — mark handled so we don't keep retrying every render.
      handledHighlightRef.current = highlightedDeviceId;
      return;
    }
    const card = lockCardRefs.current[highlightedDeviceId];
    if (!card) return;
    handledHighlightRef.current = highlightedDeviceId;
    card.scrollIntoView({ behavior: "smooth", block: "center" });
    setFlashingDeviceId(highlightedDeviceId);
    const timer = window.setTimeout(() => setFlashingDeviceId(null), 2500);
    return () => window.clearTimeout(timer);
  }, [
    highlightedDeviceId,
    viewMode,
    selectedPropertyId,
    allLocksQuery.data,
    allLocksQuery.isSuccess,
    allLocksQuery.isFetching,
    findPropertyForDeviceQuery.isFetched,
    findPropertyForDeviceQuery.data,
  ]);

  // Phase 2: once locks load for the auto-selected property, open the target lock
  useEffect(() => {
    if (deepLinkLockOpenedRef.current || !deepLinkDeviceId || locks.length === 0) return;
    const target = locks.find((l) => l.id === deepLinkDeviceId);
    if (!target) return;
    deepLinkLockOpenedRef.current = true;
    setSelectedLock(target);
    setDrawerOpen(true);
  }, [locks, deepLinkDeviceId]);

  return (
    <>
      {/* Header: back button + property name when in locks view */}
      <div className="mb-5 flex items-center gap-3">
        {viewMode === "locks" && (
          <Button
            variant="ghost"
            size="icon"
            onClick={handleBackToProperties}
            className="mr-1"
            data-testid="button-back-properties"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
        )}
        <div>
          <h2 className="text-base font-semibold text-gray-900">
            {viewMode === "locks" ? (selectedProperty?.name || "Smart Locks") : "Properties"}
          </h2>
          {viewMode === "locks" && (
            <p className="text-xs text-gray-500">
              {locks.length} smart lock{locks.length !== 1 ? "s" : ""} configured
            </p>
          )}
        </div>
      </div>

      {/* Properties list */}
      {viewMode === "properties" && (
        <>
          <div className="mb-5">
            <div className="relative max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search properties..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="input-search-properties"
              />
            </div>
          </div>

          {propertiesQuery.isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-gray-50 border border-gray-200 rounded-lg p-4 animate-pulse h-[52px]" />
              ))}
            </div>
          ) : filteredProperties.length === 0 ? (
            <div className="text-center py-16">
              <Lock className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <h3 className="text-base font-medium text-gray-600 mb-1">No properties found</h3>
              <p className="text-sm text-gray-400">
                {searchQuery ? "Try adjusting your search." : "No properties found."}
              </p>
            </div>
          ) : (
            <div className="rounded-lg border border-gray-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {([
                      { key: "name", label: "Property" },
                      { key: "address", label: "Address" },
                      { key: "rooms", label: "Rooms" },
                      { key: "locks", label: "Locks" },
                    ] as const).map((col) => (
                      <th
                        key={col.key}
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none"
                        onClick={() => {
                          if (propertySort === col.key) {
                            setPropertySortDir((d) => d === "asc" ? "desc" : "asc");
                          } else {
                            setPropertySort(col.key);
                            setPropertySortDir("asc");
                          }
                        }}
                        data-testid={`th-sort-${col.key}`}
                      >
                        <span className="flex items-center gap-1">
                          {col.label}
                          {propertySort === col.key ? (
                            propertySortDir === "asc"
                              ? <ArrowUp className="h-3 w-3 text-brand" />
                              : <ArrowDown className="h-3 w-3 text-brand" />
                          ) : (
                            <ArrowUpDown className="h-3 w-3 text-gray-400" />
                          )}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {sortedProperties.map((property) => {
                    const lockCnt = lockCountsQuery.data?.[property.id] ?? 0;
                    return (
                      <tr
                        key={property.id}
                        onClick={() => handlePropertyClick(property.id)}
                        className="hover:bg-gray-50 cursor-pointer transition-colors"
                        data-testid={`row-property-${property.id}`}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-md bg-brand/10 flex items-center justify-center flex-shrink-0">
                              <Home className="h-3.5 w-3.5 text-brand" />
                            </div>
                            <span className="font-medium text-gray-900">{property.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-500">{property.address || "—"}</td>
                        <td className="px-4 py-3 text-gray-600">{property.roomCount ?? 0}</td>
                        <td className="px-4 py-3 text-gray-600">
                          {lockCountsQuery.isLoading ? (
                            <span className="text-gray-300">…</span>
                          ) : lockCnt}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Locks list */}
      {viewMode === "locks" && (
        <>
          <div className="mb-5 flex flex-wrap items-center gap-3 justify-between">
            <div className="flex flex-wrap items-center gap-2 flex-1 min-w-[200px]">
              <div className="relative max-w-sm flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search locks..."
                  value={lockSearchQuery}
                  onChange={(e) => setLockSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="input-search-locks"
                />
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-9"
                    data-testid="button-locks-filter"
                  >
                    <Filter className="h-3.5 w-3.5 mr-1.5" />
                    Filter
                    {hasActiveFilters && (
                      <Badge
                        variant="secondary"
                        className="ml-1.5 h-4 min-w-[18px] px-1 text-[10px] bg-brand text-white"
                        data-testid="badge-filter-count"
                      >
                        {scopeFilter.size + (gatewayDisconnectedOnly ? 1 : 0) + (lowBatteryOnly ? 1 : 0)}
                      </Badge>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  <div className="px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                    Access scope
                  </div>
                  {([
                    { key: "common", label: "Common" },
                    { key: "room", label: "Room" },
                    { key: "admin", label: "Admin" },
                  ] as const).map((opt) => {
                    const checked = scopeFilter.has(opt.key);
                    return (
                      <DropdownMenuItem
                        key={opt.key}
                        onSelect={(e) => {
                          e.preventDefault();
                          setScopeFilter((prev) => {
                            const next = new Set(prev);
                            if (next.has(opt.key)) next.delete(opt.key); else next.add(opt.key);
                            return next;
                          });
                        }}
                        data-testid={`filter-scope-${opt.key}`}
                        className="flex items-center justify-between cursor-pointer"
                      >
                        <span>{opt.label}</span>
                        {checked && <Check className="h-3.5 w-3.5 text-brand" />}
                      </DropdownMenuItem>
                    );
                  })}
                  <div className="px-2 pt-2 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500 border-t border-gray-100 mt-1">
                    Health
                  </div>
                  <DropdownMenuItem
                    onSelect={(e) => { e.preventDefault(); setGatewayDisconnectedOnly((v) => !v); }}
                    data-testid="filter-gateway-disconnected"
                    className="flex items-center justify-between cursor-pointer"
                  >
                    <span className="flex items-center gap-1.5">
                      <WifiOff className="h-3.5 w-3.5 text-gray-500" />
                      Gateway disconnected
                    </span>
                    {gatewayDisconnectedOnly && <Check className="h-3.5 w-3.5 text-brand" />}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={(e) => { e.preventDefault(); setLowBatteryOnly((v) => !v); }}
                    data-testid="filter-low-battery"
                    className="flex items-center justify-between cursor-pointer"
                  >
                    <span className="flex items-center gap-1.5">
                      <BatteryLow className="h-3.5 w-3.5 text-gray-500" />
                      Battery &lt; 20%
                    </span>
                    {lowBatteryOnly && <Check className="h-3.5 w-3.5 text-brand" />}
                  </DropdownMenuItem>
                  {hasActiveFilters && (
                    <DropdownMenuItem
                      onSelect={(e) => { e.preventDefault(); clearAllFilters(); }}
                      data-testid="filter-clear"
                      className="cursor-pointer text-gray-600 border-t border-gray-100 mt-1"
                    >
                      Clear all filters
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            {selectedPropertyId && (() => {
              const propertyMute = (mutesQuery.data || []).find(
                (m) => m.propertyId === selectedPropertyId && m.condition === "all",
              );
              if (propertyMute) {
                const exp = propertyMute.expiresAt ? new Date(propertyMute.expiresAt) : null;
                return (
                  <div
                    className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-md px-3 py-1.5"
                    data-testid="badge-property-muted"
                  >
                    <BellOff className="h-3.5 w-3.5 text-gray-500" />
                    <span className="text-xs text-gray-600">
                      Property muted{exp ? ` until ${exp.toLocaleDateString()}` : ""}
                    </span>
                    {!readOnly && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-xs"
                        onClick={() => deleteMuteMutation.mutate(propertyMute.id)}
                        disabled={deleteMuteMutation.isPending}
                        data-testid="button-unmute-property"
                      >
                        <Bell className="h-3.5 w-3.5 mr-1" />
                        Unmute
                      </Button>
                    )}
                  </div>
                );
              }
              if (readOnly) return null;
              return (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid="button-mute-property"
                      disabled={createMuteMutation.isPending}
                    >
                      <BellOff className="h-3.5 w-3.5 mr-1" />
                      Mute property alerts
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {[
                      { hours: 24 as const, label: "For 24 hours" },
                      { hours: 168 as const, label: "For 7 days" },
                      { hours: 0 as const, label: "Until I unmute" },
                    ].map((opt) => (
                      <DropdownMenuItem
                        key={opt.hours}
                        onClick={() => createMuteMutation.mutate({ propertyId: selectedPropertyId, durationHours: opt.hours })}
                        data-testid={`button-mute-property-${opt.hours}`}
                      >
                        {opt.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            })()}
          </div>

          {(roomsQuery.isLoading || allLocksQuery.isLoading) ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-gray-50 border border-gray-200 rounded-xl p-4 animate-pulse h-[100px]" />
              ))}
            </div>
          ) : filteredLocks.length === 0 ? (
            <div className="text-center py-16" data-testid="empty-locks">
              <Lock className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <h3 className="text-base font-medium text-gray-600 mb-1">No locks found</h3>
              <p className="text-sm text-gray-400">
                {hasActiveFilters || lockSearchQuery
                  ? "No locks match your current filters."
                  : "No locks are linked to this property yet. Go to Connected Accounts and assign a lock to this property."}
              </p>
              {(hasActiveFilters || lockSearchQuery) && (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-4"
                  onClick={() => { clearAllFilters(); setLockSearchQuery(""); }}
                  data-testid="button-clear-filters"
                >
                  Clear filters
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredLocks.map((lock) => (
                <LockCard
                  key={lock.id}
                  lock={lock}
                  onClick={() => handleLockClick(lock)}
                />
              ))}
            </div>
          )}
        </>
      )}

      <LockDetailDrawer
        selectedLock={selectedLock}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        readOnly={readOnly}
        selectedPropertyId={selectedPropertyId}
      />
    </>
  );
}

// ─── AppDevicesSmartLocks (main page) ────────────────────────────────────────
// Now contains only: Connected Accounts tab + Settings tab.
// The Properties browser has moved to the Devices overview Smart Locks tab
// via the SmartLocksPropertyBrowser component above.

const SMART_LOCKS_TABS: SmartLocksTab[] = ["connected_accounts", "padsplit_sync", "settings"];

// Shared dismissal-gated wrapper for the Connect-a-Lock-Account dialog. Gates
// all four Radix dismissal paths (onOpenChange + the three DialogContent
// handlers) on `inFlight` so a stray backdrop click / Escape / focus shift can
// never drop a multi-second provider-auth flow (including the Seam "waiting"
// phase, which keeps polling timers + popup alive with no mutation pending).
// Mirrors SellListingDialog in app-sell.tsx. See docs/dialog-dismissal-convention.md.
export function ConnectLockDialog({
  open,
  onOpenChange,
  inFlight,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inFlight: boolean;
  children: ReactNode;
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && inFlight) return;
        onOpenChange(next);
      }}
    >
      <DialogContent
        className="sm:max-w-md max-h-[90vh] overflow-y-auto"
        onInteractOutside={(e) => {
          if (inFlight) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (inFlight) e.preventDefault();
        }}
        onPointerDownOutside={(e) => {
          if (inFlight) e.preventDefault();
        }}
      >
        {children}
      </DialogContent>
    </Dialog>
  );
}

export default function AppDevicesSmartLocks() {
  const queryClient = useQueryClient();
  usePageTitle("Smart Locks");
  const { canEdit: __canEditArea } = usePermissions();
  const readOnly = !__canEditArea("devices");
  const rawSearch = useSearch();
  const [, navigate] = useLocation();
  // Shared hook for reactive URL↔tab sync; validate against SMART_LOCKS_TABS allow-list.
  // tab-allow-list-exempt: SMART_LOCKS_TABS clamping is applied inline on the
  // next line so the hook intentionally reads the raw value here.
  const [_rawTab, setActiveTab] = useTabQueryParam("tab", "connected_accounts", { replace: true });
  const activeTab: SmartLocksTab = SMART_LOCKS_TABS.includes(_rawTab as SmartLocksTab)
    ? (_rawTab as SmartLocksTab)
    : "connected_accounts";

  useEffect(() => {
    const params = new URLSearchParams(rawSearch);
    const deviceId = params.get("deviceId");
    const propertyId = params.get("propertyId");
    // Forward property-browser deep-links to the overview where SmartLocksPropertyBrowser lives
    if (deviceId || propertyId) {
      const fwd = new URLSearchParams({ tab: "smart_locks" });
      if (deviceId) fwd.set("deviceId", deviceId);
      if (propertyId) fwd.set("propertyId", propertyId);
      navigate(`/app/devices?${fwd.toString()}`, { replace: true });
    }
  }, [rawSearch, navigate]);

  const [showConnectDialog, setShowConnectDialog] = useState(false);
  const [connectStep, setConnectStep] = useState<ConnectStep>("choose_provider");
  const [ttlockForm, setTtlockForm] = useState({ username: "", password: "" });
  const [sifelyWebhookData, setSifelyWebhookData] = useState<{ token: string; webhookUrl: string } | null>(null);
  const [sifelyForm, setSifelyForm] = useState({ clientId: "", clientSecret: "", username: "", password: "" });
  const [reconnectingConnectionId, setReconnectingConnectionId] = useState<number | null>(null);
  const [connectingSeamProvider, setConnectingSeamProvider] = useState<string | null>(null);
  const [seamConnectError, setSeamConnectError] = useState<string | null>(null);
  const seamPopupRef = useRef<Window | null>(null);
  const seamPollIntervalRef = useRef<number | null>(null);
  const seamPopupCheckRef = useRef<number | null>(null);
  const seamGracePollRef = useRef<number | null>(null);
  const seamGraceTimeoutRef = useRef<number | null>(null);
  const seamFinalizedRef = useRef<boolean>(false);
  const [activityLock, setActivityLock] = useState<{ connectionId: number; ttlockLockId: number; name: string } | null>(null);

  const { toast } = useToast();

  // Used to pass targetOwnerId to the connect-webview endpoint so connections
  // are always stored under the owner's account (not the team member's own id).
  const authMeQuery = useQuery<{ activeMembership?: { ownerUserId: number } | null }>({
    queryKey: ["/api/app/auth/me"],
    staleTime: 60 * 1000,
  });
  const activeOwnerUserId = authMeQuery.data?.activeMembership?.ownerUserId;

  const propertiesQuery = useQuery<Property[]>({
    queryKey: ["/api/app/properties"],
  });

  const ttlockConnectionsQuery = useQuery<TtlockConnectionInfo[]>({
    queryKey: ["/api/app/devices/ttlock/connections"],
  });

  const seamConnectionsQuery = useQuery<SeamConnectionItem[]>({
    queryKey: ["/api/app/devices/connections", "smart_lock"],
    queryFn: async () => {
      const res = await fetch("/api/app/devices/connections?deviceType=smart_lock", {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to fetch connections");
      return res.json() as Promise<SeamConnectionItem[]>;
    },
  });

  const connectTtlockMutation = useMutation({
    mutationFn: async () => {
      return apiRequest("POST", "/api/app/devices/ttlock/connect", {
        username: ttlockForm.username,
        password: ttlockForm.password,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/ttlock/connections"] });
      setShowConnectDialog(false);
      setTtlockForm({ username: "", password: "" });
      toast({ title: "TTLock account connected" });
    },
    onError: (err: unknown) => {
      toast({ title: extractErrMsg(err, "Failed to connect TTLock account"), variant: "destructive" });
    },
  });

  const reconnectTtlockMutation = useMutation({
    mutationFn: async (id: number) => {
      return apiRequest("PUT", `/api/app/devices/ttlock/connections/${id}/reconnect`, {
        username: ttlockForm.username,
        password: ttlockForm.password,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/ttlock/connections"] });
      setShowConnectDialog(false);
      setReconnectingConnectionId(null);
      setTtlockForm({ username: "", password: "" });
      toast({ title: "TTLock account reconnected" });
    },
    onError: (err: unknown) => {
      toast({ title: extractErrMsg(err, "Failed to reconnect TTLock account"), variant: "destructive" });
    },
  });

  const fetchSifelyWebhookTokenMutation = useMutation<{ token: string; webhookUrl: string }, unknown, { rotate?: boolean } | void>({
    meta: { silentSave: true },
    mutationFn: async (vars) => {
      const res = await apiRequest("POST", "/api/app/devices/sifely/webhook-token", { rotate: vars?.rotate === true });
      return res.json() as Promise<{ token: string; webhookUrl: string }>;
    },
    onSuccess: (data, vars) => {
      setSifelyWebhookData(data);
      // A plain fetch advances into Step 1; an explicit rotate happens while the
      // host is already on Step 1, so just swap the URL in place.
      if (!vars?.rotate) setConnectStep("sifely_step1");
      else toast({ title: "Webhook URL rotated — re-paste it into Sifely" });
    },
    onError: (err: unknown) => {
      toast({ title: extractErrMsg(err, "Failed to generate Sifely webhook URL"), variant: "destructive" });
    },
  });

  const connectSifelyMutation = useMutation({
    mutationFn: async () => {
      if (!sifelyWebhookData) throw new Error("Webhook token missing");
      return apiRequest("POST", "/api/app/devices/sifely/connect", {
        clientId: sifelyForm.clientId.trim(),
        clientSecret: sifelyForm.clientSecret.trim(),
        username: sifelyForm.username.trim(),
        password: sifelyForm.password,
        webhookToken: sifelyWebhookData.token,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/ttlock/connections"] });
      setShowConnectDialog(false);
      setSifelyForm({ clientId: "", clientSecret: "", username: "", password: "" });
      setSifelyWebhookData(null);
      toast({ title: "Sifely account connected" });
    },
    onError: (err: unknown) => {
      toast({ title: extractErrMsg(err, "Failed to connect Sifely account"), variant: "destructive" });
    },
  });

  const reconnectSifelyMutation = useMutation({
    mutationFn: async (id: number) => {
      return apiRequest("PUT", `/api/app/devices/ttlock/connections/${id}/sifely-reconnect`, {
        clientId: sifelyForm.clientId.trim(),
        clientSecret: sifelyForm.clientSecret.trim(),
        username: sifelyForm.username.trim(),
        password: sifelyForm.password,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/ttlock/connections"] });
      setShowConnectDialog(false);
      setReconnectingConnectionId(null);
      setSifelyForm({ clientId: "", clientSecret: "", username: "", password: "" });
      toast({ title: "Sifely account reconnected" });
    },
    onError: (err: unknown) => {
      toast({ title: extractErrMsg(err, "Failed to reconnect Sifely account"), variant: "destructive" });
    },
  });

  const disconnectTtlockMutation = useMutation({
    mutationFn: async (id: number) => {
      return apiRequest("DELETE", `/api/app/devices/ttlock/connections/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/ttlock/connections"] });
      toast({ title: "TTLock account disconnected" });
    },
    onError: () => {
      toast({ title: "Failed to disconnect TTLock account", variant: "destructive" });
    },
  });

  function clearSeamConnectTimers() {
    if (seamPollIntervalRef.current !== null) {
      window.clearInterval(seamPollIntervalRef.current);
      seamPollIntervalRef.current = null;
    }
    if (seamPopupCheckRef.current !== null) {
      window.clearInterval(seamPopupCheckRef.current);
      seamPopupCheckRef.current = null;
    }
    if (seamGracePollRef.current !== null) {
      window.clearInterval(seamGracePollRef.current);
      seamGracePollRef.current = null;
    }
    if (seamGraceTimeoutRef.current !== null) {
      window.clearTimeout(seamGraceTimeoutRef.current);
      seamGraceTimeoutRef.current = null;
    }
  }

  function resetSeamConnectFlow() {
    clearSeamConnectTimers();
    if (seamPopupRef.current && !seamPopupRef.current.closed) {
      try { seamPopupRef.current.close(); } catch {}
    }
    seamPopupRef.current = null;
    setConnectStep("choose_provider");
    setConnectingSeamProvider(null);
    setSeamConnectError(null);
  }

  useEffect(() => {
    return () => clearSeamConnectTimers();
  }, []);

  const connectSeamWebviewMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async (provider: string) => {
      const res = await apiRequest("POST", "/api/app/devices/seam/connect-webview", {
        provider,
        deviceType: "smart_lock",
        // Explicitly pass the owner's userId so the server stores the connection
        // under the owner's account even when initiated by a team member.
        // For direct owners activeOwnerUserId is undefined (not sent) and the
        // server auto-resolves correctly.
        ...(activeOwnerUserId != null ? { targetOwnerId: activeOwnerUserId } : {}),
      });
      return res.json() as Promise<{ connectWebviewId: string; url: string; provider: string }>;
    },
    onSuccess: (data) => {
      setConnectingSeamProvider(data.provider);
      setConnectStep("seam_waiting");
      setSeamConnectError(null);
      seamFinalizedRef.current = false;

      const popup = window.open(
        data.url,
        "seam-connect",
        "popup,width=600,height=750,resizable=yes,scrollbars=yes",
      );
      seamPopupRef.current = popup;
      if (!popup) {
        setSeamConnectError("Popup blocked. Please allow popups for this site and try again.");
        return;
      }

      // Runs the success path exactly once, guarded so concurrent poll ticks
      // can't double-toast or double-invalidate.
      const finalizeSuccess = () => {
        if (seamFinalizedRef.current) return;
        seamFinalizedRef.current = true;
        clearSeamConnectTimers();
        if (seamPopupRef.current && !seamPopupRef.current.closed) {
          try { seamPopupRef.current.close(); } catch {}
        }
        seamPopupRef.current = null;
        queryClient.invalidateQueries({ queryKey: ["/api/app/devices/connections"] });
        setShowConnectDialog(false);
        resetSeamConnectFlow();
        toast({ title: "Lock account connected successfully" });
      };

      const checkStatus = async (): Promise<boolean> => {
        if (seamFinalizedRef.current) return true;
        try {
          const res = await fetch(
            `/api/app/devices/seam/connect-webview/${encodeURIComponent(data.connectWebviewId)}`,
            { credentials: "include" },
          );
          if (!res.ok) return false;
          const body = (await res.json()) as { loginSuccessful: boolean };
          if (body.loginSuccessful) {
            finalizeSuccess();
            return true;
          }
          return false;
        } catch {
          return false;
        }
      };

      // Main poll while the popup is open — runs every 2.5s
      seamPollIntervalRef.current = window.setInterval(() => {
        void checkStatus();
      }, 2500);

      // Popup-closed watcher — fires every 1s
      seamPopupCheckRef.current = window.setInterval(() => {
        if (seamPopupRef.current && seamPopupRef.current.closed) {
          // Stop the popup watcher and the main poll
          if (seamPopupCheckRef.current !== null) {
            window.clearInterval(seamPopupCheckRef.current);
            seamPopupCheckRef.current = null;
          }
          if (seamPollIntervalRef.current !== null) {
            window.clearInterval(seamPollIntervalRef.current);
            seamPollIntervalRef.current = null;
          }
          seamPopupRef.current = null;

          // Seam often auto-closes the popup the instant the user finishes
          // its hosted login — potentially before our 2.5s poll fires. Keep
          // polling for up to 20s so Seam has time to flip `login_successful`
          // and assign a connected_account_id before we give up.
          seamGracePollRef.current = window.setInterval(() => {
            void checkStatus();
          }, 2500);

          seamGraceTimeoutRef.current = window.setTimeout(() => {
            if (seamFinalizedRef.current) return;
            if (seamGracePollRef.current !== null) {
              window.clearInterval(seamGracePollRef.current);
              seamGracePollRef.current = null;
            }
            // User closed the popup without completing login, or Seam took
            // longer than 45s to flip login_successful. Show a sync prompt
            // so the host can recover without re-opening the flow.
            setConnectStep("seam_picker");
            setConnectingSeamProvider(null);
            setSeamConnectError("sync_prompt");
          }, 45000);
        }
      }, 1000);
    },
    onError: (error: Error) => {
      setSeamConnectError(parseErrorMessage(error, "Failed to start connect flow"));
      toast({ title: error.message || "Failed to connect account", variant: "destructive" });
    },
  });

  const disconnectSeamMutation = useMutation({
    mutationFn: async (id: number) => {
      return apiRequest("DELETE", `/api/app/devices/connections/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/connections"] });
      toast({ title: "Account disconnected" });
    },
    onError: () => {
      toast({ title: "Failed to disconnect account", variant: "destructive" });
    },
  });

  const syncSeamAccountsMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/app/devices/seam/sync-accounts", {});
      return res.json() as Promise<{ imported: number; alreadyPresent: number; skipped: number }>;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/connections"] });
      if (data.imported > 0) {
        toast({
          title: `Sync complete — ${data.imported} new account${data.imported === 1 ? "" : "s"} imported`,
        });
      } else {
        toast({ title: "Sync complete — no new accounts found" });
      }
    },
    onError: () => {
      toast({ title: "Sync failed — please try again", variant: "destructive" });
    },
  });

  // Task #1733 — Toggle the ignored flag for a Seam lock via
  // PUT /api/app/devices/seam/locks/:deviceId/ignore
  const seamIgnoreMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async ({ deviceId, ignored }: { deviceId: string; ignored: boolean }) => {
      return apiRequest("PUT", `/api/app/devices/seam/locks/${encodeURIComponent(deviceId)}/ignore`, { ignored });
    },
    onSuccess: (_data, { ignored }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/seam/locks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/locks/list-for-property"] });
      // Task #2242 — refresh the connections list so the unassigned-locks
      // callout count reflects ignore/restore in real time.
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/connections"] });
      toast({ title: ignored ? "Lock ignored — excluded from all properties" : "Lock restored" });
    },
    onError: (err: unknown) => {
      toast({ title: extractErrMsg(err, "Failed to update lock"), variant: "destructive" });
    },
  });

  // Task #2242 — Pin a Seam lock to a property or room via
  // PATCH /api/app/devices/seam/locks/:deviceId. Passing both null clears
  // the pin entirely.
  const seamAssignMutation = useMutation({
    mutationFn: async ({ deviceId, propertyId, roomId }: { deviceId: string; propertyId: number | null; roomId: number | null }) => {
      return apiRequest("PATCH", `/api/app/devices/seam/locks/${encodeURIComponent(deviceId)}`, { propertyId, roomId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/seam/locks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/locks/list-for-property"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/connections"] });
    },
    onError: (err: unknown) => {
      toast({ title: extractErrMsg(err, "Failed to save assignment"), variant: "destructive" });
    },
  });

  const [expandedSeamConnIds, setExpandedSeamConnIds] = useState<Set<number>>(new Set());
  const toggleSeamConnExpanded = (id: number) => {
    setExpandedSeamConnIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Any in-flight connect/reconnect step (multi-second provider auth + webhook
  // token fetch) must block dismissal of the Connect-a-Lock-Account dialog so a
  // stray backdrop click / Escape / focus shift never drops the flow mid-auth.
  // This includes the Seam "waiting" phase: after connectSeamWebviewMutation
  // *resolves*, the popup auth + polling timers are still running with no
  // mutation pending, so the step itself (not just isPending) must count as
  // in-flight or a stray dismissal would call resetSeamConnectFlow() mid-auth.
  const connectDialogInFlight =
    connectTtlockMutation.isPending ||
    reconnectTtlockMutation.isPending ||
    fetchSifelyWebhookTokenMutation.isPending ||
    connectSifelyMutation.isPending ||
    reconnectSifelyMutation.isPending ||
    connectSeamWebviewMutation.isPending ||
    connectStep === "seam_waiting";

  const activeProperties = sortProperties((propertiesQuery.data || []).filter(
    (p) => p.status !== "archive"
  ));
  const ttlockConnections = ttlockConnectionsQuery.data || [];
  const seamConnections = seamConnectionsQuery.data || [];
  const expiredTtlockConns = ttlockConnections.filter((c) => c.status === "token_expired" || c.status === "error");
  // Task #2413 — Seam fires connected_account.disconnected/deleted when a host
  // revokes PadPulse access in their lock provider's app; the webhook flips
  // status to 'disconnected'. Surface this so hosts know to reconnect.
  const disconnectedSeamConns = seamConnections.filter((c) => c.status === "disconnected");

  // Task #2235 / #2242 — Surface unassigned TTLock + Seam locks so hosts know
  // to assign them. Unassigned locks are silently skipped by the health poller
  // (Task #2228), so without this callout a host who forgets to assign a new
  // lock would never see any battery/offline alerts for it.
  const unassignedTtlockTotal = ttlockConnections.reduce(
    (sum, c) => sum + (c.unassignedLockCount ?? 0),
    0,
  );
  const unassignedSeamTotal = seamConnections.reduce(
    (sum, c) => sum + (c.unassignedLockCount ?? 0),
    0,
  );
  const unassignedLockTotal = unassignedTtlockTotal + unassignedSeamTotal;
  const [ttlockForceExpandSignal, setTtlockForceExpandSignal] = useState(0);
  const firstUnassignedCardRef = useRef<HTMLDivElement | null>(null);
  const firstUnassignedSeamRowRef = useRef<HTMLTableRowElement | null>(null);
  const handleUnassignedCalloutClick = () => {
    setActiveTab("connected_accounts");
    setTtlockForceExpandSignal((n) => n + 1);
    // Task #2242 — also auto-expand every Seam connection that has
    // unassigned locks so the picker is visible immediately.
    if (unassignedSeamTotal > 0) {
      setExpandedSeamConnIds((prev) => {
        const next = new Set(prev);
        for (const c of seamConnections) {
          if ((c.unassignedLockCount ?? 0) > 0) next.add(c.id);
        }
        return next;
      });
    }
    // Scroll the first connection with unassigned locks into view after
    // the click triggers expansion. Prefer TTLock card; fall back to the
    // first Seam row if no TTLock connection has unassigned locks.
    requestAnimationFrame(() => {
      const target = firstUnassignedCardRef.current ?? firstUnassignedSeamRowRef.current;
      target?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };
  const firstUnassignedConnId = ttlockConnections.find(
    (c) => (c.unassignedLockCount ?? 0) > 0,
  )?.id;
  const firstUnassignedSeamConnId = seamConnections.find(
    (c) => (c.unassignedLockCount ?? 0) > 0,
  )?.id;

  return (
    <AppLayout>
      <div className="p-6">
        <ViewOnlyBanner area="devices" />
        <div className="mb-6 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div>
              <h1 className="text-2xl font-bold text-gray-900" data-tour="smart-locks-heading">
                Smart Locks
              </h1>
              <p className="text-sm text-gray-500 mt-1">Manage your smart locks and connected accounts.</p>
            </div>
            <div className="flex items-center gap-2">
              <ExplainerVideoButton pageKey="smart-locks" />
              <GuideButton pageKey="smart-locks" />
            </div>
          </div>
          <GuideNudgeBanner pageKey="smart-locks" />
          {activeTab === "connected_accounts" && (
            <Button
              disabled={readOnly}
              className="bg-brand hover:bg-brand-hover text-white"
              onClick={() => { setConnectStep("choose_provider"); setShowConnectDialog(true); }}
              data-testid="button-connect-account"
              data-tour="smart-locks-connect"
            >
              <Plus className="h-4 w-4 mr-1" />
              Connect Account
            </Button>
          )}
        </div>

        {unassignedLockTotal > 0 && (
          <button
            type="button"
            onClick={handleUnassignedCalloutClick}
            className="mb-4 w-full p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-3 text-left hover:bg-amber-100 transition-colors"
            data-testid="button-unassigned-locks-callout"
          >
            <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0" />
            <p className="text-sm text-amber-800 flex-1" data-testid="text-unassigned-locks-callout">
              {unassignedLockTotal === 1
                ? "1 lock is unassigned — assign it to a property to receive battery and offline alerts."
                : `${unassignedLockTotal} locks are unassigned — assign them to a property to receive battery and offline alerts.`}
            </p>
            <span className="text-xs font-medium text-amber-800 inline-flex items-center gap-1">
              Assign now
              <ChevronRight className="h-3.5 w-3.5" />
            </span>
          </button>
        )}

        {disconnectedSeamConns.length > 0 && (
          <div
            className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-3"
            data-testid="callout-seam-disconnected"
          >
            <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0" />
            <p className="text-sm text-amber-800">
              {disconnectedSeamConns.length === 1
                ? "A lock account was revoked from its provider app. Reconnect it to keep managing locks."
                : `${disconnectedSeamConns.length} lock accounts were revoked from their provider apps. Reconnect them to keep managing locks.`}
            </p>
            <Button
              size="sm"
              variant="outline"
              className="ml-auto border-amber-300 text-amber-800 hover:bg-amber-100"
              onClick={() => { setActiveTab("connected_accounts"); }}
              data-testid="button-reconnect-seam-banner"
            >
              <RefreshCw className="h-3.5 w-3.5 mr-1" />
              Reconnect
            </Button>
          </div>
        )}

        {expiredTtlockConns.length > 0 && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-3">
            <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0" />
            <p className="text-sm text-amber-800">
              {expiredTtlockConns.length === 1
                ? "Your TTLock account needs to be reconnected."
                : `${expiredTtlockConns.length} TTLock accounts need to be reconnected.`}
            </p>
            <Button
              size="sm"
              variant="outline"
              className="ml-auto border-amber-300 text-amber-800 hover:bg-amber-100"
              onClick={() => { setActiveTab("connected_accounts"); }}
              data-testid="button-reconnect-ttlock-banner"
            >
              <RefreshCw className="h-3.5 w-3.5 mr-1" />
              Reconnect
            </Button>
          </div>
        )}

        <div className="border-b border-gray-200 mb-6">
          <div className="flex gap-0">
            {([
              { key: "connected_accounts" as SmartLocksTab, label: "Connected Accounts" },
              { key: "padsplit_sync" as SmartLocksTab, label: "PadSplit Sync" },
              { key: "settings" as SmartLocksTab, label: "Settings" },
            ]).map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? "border-brand text-brand"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
                data-testid={`tab-smart-locks-${tab.key}`}
                {...(tab.key === "settings" ? { "data-tour": "smart-locks-tab-settings" } : {})}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {activeTab === "connected_accounts" && (
          <div className="space-y-8">
            <div data-tour="smart-locks-list">
              <h2 className="text-base font-semibold text-gray-900 mb-1">TTLock / Sifely Accounts</h2>
              <p className="text-sm text-gray-500 mb-4">
                Connect your TTLock or Sifely account to manage those locks directly — free with your PadPulse subscription.
              </p>
              {ttlockConnectionsQuery.isLoading ? (
                <div className="space-y-3">
                  {[1, 2].map((i) => <div key={i} className="border border-gray-200 rounded-xl p-4 animate-pulse h-16" />)}
                </div>
              ) : ttlockConnections.length === 0 ? (
                <div className="border border-gray-200 rounded-xl p-8 text-center">
                  <Zap className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-gray-500">No TTLock accounts connected yet.</p>
                  <p className="text-xs text-gray-400 mt-1">Click "Connect Account" to link your TTLock or Sifely account.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {ttlockConnections.map((conn) => (
                    <TtlockConnectionCard
                      key={conn.id}
                      conn={conn}
                      readOnly={readOnly}
                      activeProperties={activeProperties}
                      onReconnect={(c) => {
                        setReconnectingConnectionId(c.id);
                        if (c.provider === "sifely") {
                          setSifelyForm({ clientId: "", clientSecret: "", username: c.ttlockUsername, password: "" });
                          setConnectStep("sifely_step2");
                        } else {
                          setTtlockForm({ username: c.ttlockUsername, password: "" });
                          setConnectStep("ttlock_form");
                        }
                        setShowConnectDialog(true);
                      }}
                      onDisconnect={(id) => disconnectTtlockMutation.mutate(id)}
                      onActivityOpen={(args) => setActivityLock(args)}
                      forceExpandSignal={
                        (conn.unassignedLockCount ?? 0) > 0 ? ttlockForceExpandSignal : 0
                      }
                      cardRef={
                        conn.id === firstUnassignedConnId
                          ? (el) => { firstUnassignedCardRef.current = el; }
                          : undefined
                      }
                    />
                  ))}
                </div>
              )}
            </div>

            <div data-tour="smart-locks-other-brands">
              <div className="flex items-start justify-between mb-1">
                <h2 className="text-base font-semibold text-gray-900">Other Brand Accounts</h2>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-xs h-7 px-2.5"
                  disabled={syncSeamAccountsMutation.isPending || readOnly}
                  onClick={() => syncSeamAccountsMutation.mutate()}
                  data-testid="button-seam-sync-accounts"
                >
                  {syncSeamAccountsMutation.isPending ? (
                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                  ) : (
                    <RefreshCw className="h-3 w-3 mr-1" />
                  )}
                  Sync Other Locks
                </Button>
              </div>
              <p className="text-sm text-gray-500 mb-1">
                For August, Yale, Schlage, and other non-TTLock brands.
              </p>
              <p className="text-sm font-medium text-amber-700 mb-4">
                $2.50/month per connected lock applies.
              </p>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Provider</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Account</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {seamConnectionsQuery.isLoading ? (
                      <tr>
                        <td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-400">Loading...</td>
                      </tr>
                    ) : seamConnections.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-400">
                          No other lock accounts connected yet.
                        </td>
                      </tr>
                    ) : seamConnections.map((conn) => {
                      const expanded = expandedSeamConnIds.has(conn.id);
                      const isFirstUnassignedSeam = conn.id === firstUnassignedSeamConnId;
                      return (
                      <Fragment key={conn.id}>
                      <tr
                        ref={isFirstUnassignedSeam ? firstUnassignedSeamRowRef : undefined}
                        className="hover:bg-gray-50"
                        data-testid={`row-seam-connection-${conn.id}`}
                      >
                        <td className="px-4 py-3 text-gray-900 font-medium capitalize">
                          <span className="inline-flex items-center gap-2">
                            <Lock className="h-4 w-4 text-gray-400" />
                            {conn.provider}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-600">{conn.accountEmail || "—"}</td>
                        <td className="px-4 py-3">
                          {conn.status === "disconnected" ? (
                            <span
                              className="text-amber-700 text-sm"
                              data-testid={`status-seam-disconnected-${conn.id}`}
                            >
                              Disconnected
                            </span>
                          ) : (
                            <span className="text-green-600 text-sm">Connected</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-xs h-7 px-2.5"
                              onClick={() => toggleSeamConnExpanded(conn.id)}
                              data-testid={`button-expand-seam-${conn.id}`}
                            >
                              <ChevronRight className={`h-3.5 w-3.5 mr-1 transition-transform ${expanded ? "rotate-90" : ""}`} />
                              {expanded ? "Hide locks" : "See locks"}
                            </Button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button className="p-1 hover:bg-gray-100 rounded" data-testid={`button-seam-actions-${conn.id}`}>
                                  <MoreHorizontal className="h-4 w-4 text-gray-500" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  disabled={readOnly}
                                  className="text-red-600"
                                  onClick={() => disconnectSeamMutation.mutate(conn.id)}
                                  data-testid={`button-seam-disconnect-${conn.id}`}
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Disconnect
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </td>
                      </tr>
                      {expanded && (
                        <tr className="bg-gray-50" data-testid={`row-seam-connection-locks-${conn.id}`}>
                          <td colSpan={4} className="px-4 py-3">
                            <SeamConnectionLocksPanel
                              connectionId={conn.id}
                              readOnly={readOnly}
                              activeProperties={activeProperties}
                              onIgnore={(deviceId, ignored) => seamIgnoreMutation.mutate({ deviceId, ignored })}
                              onAssign={(deviceId, propertyId, roomId) =>
                                seamAssignMutation.mutate({ deviceId, propertyId, roomId })
                              }
                            />
                          </td>
                        </tr>
                      )}
                      </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === "padsplit_sync" && (
          <PadsplitSyncTab readOnly={readOnly} />
        )}

        {activeTab === "settings" && (
          <DeviceSettingsPanel />
        )}
      </div>

      <ConnectLockDialog
        open={showConnectDialog}
        inFlight={connectDialogInFlight}
        onOpenChange={(open) => {
          setShowConnectDialog(open);
          if (!open) {
            setConnectStep("choose_provider");
            setTtlockForm({ username: "", password: "" });
            setSifelyForm({ clientId: "", clientSecret: "", username: "", password: "" });
            setSifelyWebhookData(null);
            setReconnectingConnectionId(null);
            resetSeamConnectFlow();
          }
        }}
      >
          <DialogHeader>
            <DialogTitle>
              {connectStep === "choose_provider" && "Connect a Lock Account"}
              {connectStep === "ttlock_form" && (reconnectingConnectionId !== null ? "Reconnect TTLock Account" : "Connect TTLock Account")}
              {connectStep === "sifely_step1" && "Connect Sifely — Step 1 of 2"}
              {connectStep === "sifely_step2" && (reconnectingConnectionId !== null ? "Reconnect Sifely Account" : "Connect Sifely — Step 2 of 2")}
              {connectStep === "seam_picker" && "Connect a Lock Account"}
              {connectStep === "seam_waiting" && "Connecting…"}
            </DialogTitle>
          </DialogHeader>

          {connectStep === "choose_provider" && (
            <div className="space-y-3 pt-2">
              <button
                className="w-full p-4 border-2 border-brand rounded-lg text-left hover:bg-brand/5 transition-colors"
                onClick={() => setConnectStep("ttlock_form")}
                data-testid="button-choose-ttlock"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <Zap className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-gray-900">TTLock</p>
                      <Badge className="bg-blue-100 text-blue-700 text-[10px] px-1.5">Recommended · Free</Badge>
                    </div>
                    <p className="text-sm text-gray-500 mt-0.5">
                      Sign in with your TTLock username and password. No API key needed.
                    </p>
                  </div>
                </div>
              </button>

              <button
                className="w-full p-4 border-2 border-brand rounded-lg text-left hover:bg-brand/5 transition-colors"
                onClick={() => {
                  setSifelyForm({ clientId: "", clientSecret: "", username: "", password: "" });
                  fetchSifelyWebhookTokenMutation.mutate();
                }}
                disabled={fetchSifelyWebhookTokenMutation.isPending}
                data-testid="button-choose-sifely"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                    {fetchSifelyWebhookTokenMutation.isPending
                      ? <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
                      : <Zap className="h-5 w-5 text-blue-600" />
                    }
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-gray-900">Sifely</p>
                      <Badge className="bg-blue-100 text-blue-700 text-[10px] px-1.5">Free · Requires dev app</Badge>
                    </div>
                    <p className="text-sm text-gray-500 mt-0.5">
                      Uses your own Sifely developer app credentials. Guided setup.
                    </p>
                  </div>
                </div>
              </button>

              <button
                className="w-full p-4 border border-gray-200 rounded-lg text-left hover:border-gray-300 hover:bg-gray-50 transition-colors"
                onClick={() => setConnectStep("seam_picker")}
                data-testid="button-choose-seam"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <Lock className="h-5 w-5 text-gray-600" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-gray-900">Other brands</p>
                      <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-50 text-[10px] px-1.5">$2.50/lock/month</Badge>
                    </div>
                    <p className="text-sm text-gray-500 mt-0.5">
                      For August, Yale, Schlage, and other non-TTLock brands.
                    </p>
                  </div>
                </div>
              </button>
            </div>
          )}

          {connectStep === "sifely_step1" && sifelyWebhookData && (
            <div className="space-y-4 pt-2">
              <p className="text-sm text-gray-600">
                Sifely requires you to register your own developer app so PadPulse can receive real-time lock events. This takes about 2 minutes.
              </p>
              <ol className="space-y-3 text-sm text-gray-700 list-none">
                <li className="flex gap-2">
                  <span className="w-5 h-5 rounded-full bg-brand text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">1</span>
                  <span>
                    Go to{" "}
                    <a
                      href="https://app-smart-manager.sifely.com/Login.html"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-brand underline"
                      data-testid="link-sifely-developer-portal"
                    >
                      app-smart-manager.sifely.com
                    </a>{" "}
                    and log in with your Sifely account.
                  </span>
                </li>
                <li className="flex gap-2">
                  <span className="w-5 h-5 rounded-full bg-brand text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">2</span>
                  <span>Create a new developer app. When asked for the <strong>Webhook URL</strong>, paste the URL below.</span>
                </li>
              </ol>
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                <p className="text-[11px] text-gray-500 mb-1 font-medium uppercase tracking-wider">Your Webhook URL</p>
                <div className="flex items-center gap-2">
                  <code className="text-xs text-gray-800 break-all flex-1">{sifelyWebhookData.webhookUrl}</code>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 px-2 flex-shrink-0"
                    onClick={() => {
                      navigator.clipboard.writeText(sifelyWebhookData.webhookUrl);
                      toast({ title: "Webhook URL copied" });
                    }}
                    data-testid="button-copy-sifely-webhook-url"
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                </div>
                <div className="flex items-center justify-between gap-2 mt-2">
                  <p className="text-[11px] text-gray-400">This URL stays the same every time — paste it once.</p>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 px-1.5 flex-shrink-0 text-[11px] text-gray-500 hover:text-gray-700"
                    onClick={() => fetchSifelyWebhookTokenMutation.mutate({ rotate: true })}
                    disabled={fetchSifelyWebhookTokenMutation.isPending}
                    data-testid="button-rotate-sifely-webhook-url"
                  >
                    <RefreshCw className={`h-3 w-3 mr-1 ${fetchSifelyWebhookTokenMutation.isPending ? "animate-spin" : ""}`} />
                    Rotate webhook
                  </Button>
                </div>
              </div>
              <ol className="space-y-3 text-sm text-gray-700 list-none" start={3}>
                <li className="flex gap-2">
                  <span className="w-5 h-5 rounded-full bg-brand text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">3</span>
                  <span>After saving the app, Sifely will show you a <strong>Client ID</strong> and <strong>Client Secret</strong>. Copy both — you'll paste them in the next step along with your Sifely account email and password.</span>
                </li>
              </ol>
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setConnectStep("choose_provider")}
                >
                  Back
                </Button>
                <Button
                  className="flex-1 bg-brand hover:bg-brand-hover text-white"
                  onClick={() => setConnectStep("sifely_step2")}
                  data-testid="button-sifely-step1-next"
                >
                  I've got my credentials →
                </Button>
              </div>
            </div>
          )}

          {connectStep === "sifely_step2" && (
            <div className="space-y-4 pt-2">
              {reconnectingConnectionId === null && (
                <p className="text-sm text-gray-500">
                  Paste the <strong>Client ID</strong> and <strong>Client Secret</strong> from your Sifely developer app, then enter your Sifely account email/phone and password.
                </p>
              )}
              {reconnectingConnectionId !== null && (
                <p className="text-sm text-gray-500">
                  Your Sifely session expired. Re-enter your credentials to restore access.
                </p>
              )}
              <div className="space-y-2">
                <Label htmlFor="sifely-client-id">Client ID</Label>
                <Input
                  id="sifely-client-id"
                  type="text"
                  placeholder="Sifely developer app Client ID"
                  value={sifelyForm.clientId}
                  onChange={(e) => setSifelyForm((f) => ({ ...f, clientId: e.target.value }))}
                  data-testid="input-sifely-client-id"
                />
                <p className="text-xs text-gray-400">
                  This is the generated <strong>Client ID</strong> code from your Sifely developer app — not the app's name. Copy it exactly, with no extra spaces.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="sifely-client-secret">Client Secret</Label>
                <Input
                  id="sifely-client-secret"
                  type="password"
                  placeholder="Sifely developer app Client Secret"
                  value={sifelyForm.clientSecret}
                  onChange={(e) => setSifelyForm((f) => ({ ...f, clientSecret: e.target.value }))}
                  data-testid="input-sifely-client-secret"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sifely-username">Sifely Account Email / Phone</Label>
                <Input
                  id="sifely-username"
                  type="text"
                  placeholder="Email or phone used to log in to Sifely"
                  value={sifelyForm.username}
                  onChange={(e) => setSifelyForm((f) => ({ ...f, username: e.target.value }))}
                  data-testid="input-sifely-username"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sifely-password">Sifely Account Password</Label>
                <Input
                  id="sifely-password"
                  type="password"
                  placeholder="Your Sifely account password"
                  value={sifelyForm.password}
                  onChange={(e) => setSifelyForm((f) => ({ ...f, password: e.target.value }))}
                  data-testid="input-sifely-password"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    if (reconnectingConnectionId !== null) {
                      setShowConnectDialog(false);
                    } else {
                      setConnectStep("sifely_step1");
                    }
                  }}
                >
                  {reconnectingConnectionId !== null ? "Cancel" : "Back"}
                </Button>
                <Button
                  className="flex-1 bg-brand hover:bg-brand-hover text-white"
                  disabled={
                    !sifelyForm.clientId ||
                    !sifelyForm.clientSecret ||
                    !sifelyForm.username ||
                    !sifelyForm.password ||
                    connectSifelyMutation.isPending ||
                    reconnectSifelyMutation.isPending
                  }
                  onClick={() => {
                    if (reconnectingConnectionId !== null) {
                      reconnectSifelyMutation.mutate(reconnectingConnectionId);
                    } else {
                      connectSifelyMutation.mutate();
                    }
                  }}
                  data-testid="button-sifely-connect-submit"
                >
                  {(connectSifelyMutation.isPending || reconnectSifelyMutation.isPending)
                    ? reconnectingConnectionId !== null ? "Reconnecting..." : "Connecting..."
                    : reconnectingConnectionId !== null ? "Reconnect" : "Connect Sifely"}
                </Button>
              </div>
            </div>
          )}

          {connectStep === "ttlock_form" && (
            <div className="space-y-4 pt-2">
              <p className="text-sm text-gray-500">
                Enter your TTLock app credentials. We'll connect it directly — no API key needed.
              </p>
              <div className="space-y-2">
                <Label htmlFor="ttlock-username">TTLock / Sifely Username</Label>
                <Input
                  id="ttlock-username"
                  type="text"
                  placeholder="Your TTLock or Sifely username or email"
                  value={ttlockForm.username}
                  onChange={(e) => setTtlockForm((f) => ({ ...f, username: e.target.value }))}
                  data-testid="input-ttlock-username"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ttlock-password">Password</Label>
                <Input
                  id="ttlock-password"
                  type="password"
                  placeholder="Your TTLock password"
                  value={ttlockForm.password}
                  onChange={(e) => setTtlockForm((f) => ({ ...f, password: e.target.value }))}
                  data-testid="input-ttlock-password"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    if (reconnectingConnectionId !== null) {
                      setShowConnectDialog(false);
                    } else {
                      setConnectStep("choose_provider");
                    }
                  }}
                >
                  {reconnectingConnectionId !== null ? "Cancel" : "Back"}
                </Button>
                <Button
                  className="flex-1 bg-brand hover:bg-brand-hover text-white"
                  disabled={
                    !ttlockForm.username ||
                    !ttlockForm.password ||
                    connectTtlockMutation.isPending ||
                    reconnectTtlockMutation.isPending
                  }
                  onClick={() => {
                    if (reconnectingConnectionId !== null) {
                      reconnectTtlockMutation.mutate(reconnectingConnectionId);
                    } else {
                      connectTtlockMutation.mutate();
                    }
                  }}
                  data-testid="button-ttlock-connect-submit"
                >
                  {(connectTtlockMutation.isPending || reconnectTtlockMutation.isPending)
                    ? reconnectingConnectionId !== null ? "Reconnecting..." : "Connecting..."
                    : reconnectingConnectionId !== null ? "Reconnect" : "Connect"}
                </Button>
              </div>
            </div>
          )}

          {connectStep === "seam_picker" && (
            <div className="space-y-3 mt-2">
              <p className="text-sm text-gray-500">
                Choose your lock brand. You'll sign in with that account in a popup window and your locks will appear automatically.
              </p>
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                Each connected lock costs <strong>$2.50/month</strong> and will appear on your billing page.
              </div>
              {([
                { provider: "august", label: "August" },
                { provider: "yale", label: "Yale" },
                { provider: "schlage", label: "Schlage" },
                { provider: "kwikset", label: "Kwikset" },
                { provider: "nuki", label: "Nuki" },
                { provider: "igloo", label: "Igloo" },
              ]).map((b) => (
                <button
                  key={b.provider}
                  onClick={() => connectSeamWebviewMutation.mutate(b.provider)}
                  disabled={readOnly || connectSeamWebviewMutation.isPending}
                  className="w-full flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-brand hover:bg-brand-subtle transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid={`button-lock-provider-${b.provider}`}
                >
                  <span className="flex items-center gap-3">
                    <Lock className="h-5 w-5 text-gray-400" />
                    <span className="font-medium text-gray-900">{b.label}</span>
                  </span>
                  {connectSeamWebviewMutation.isPending && connectingSeamProvider === b.provider ? (
                    <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                  ) : (
                    <span className="text-xs text-gray-400">Connect →</span>
                  )}
                </button>
              ))}
              {seamConnectError === "sync_prompt" ? (
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 space-y-2">
                  <p className="text-sm text-blue-800" data-testid="text-seam-connect-error">
                    Didn't see a new connection? Try syncing — if you completed the lock account setup it may just need a moment.
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-blue-300 text-blue-800 hover:bg-blue-100"
                    disabled={syncSeamAccountsMutation.isPending}
                    onClick={() => {
                      syncSeamAccountsMutation.mutate(undefined, {
                        onSuccess: () => setShowConnectDialog(false),
                      });
                    }}
                    data-testid="button-seam-sync-inline"
                  >
                    {syncSeamAccountsMutation.isPending ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                    ) : (
                      <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                    )}
                    Sync Other Locks
                  </Button>
                </div>
              ) : seamConnectError ? (
                <p className="text-sm text-red-600" data-testid="text-seam-connect-error">{seamConnectError}</p>
              ) : null}
              <div className="flex justify-between pt-2">
                <Button
                  variant="outline"
                  onClick={() => setConnectStep("choose_provider")}
                  data-testid="button-seam-picker-back"
                >
                  Back
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowConnectDialog(false)}
                  data-testid="button-seam-picker-cancel"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {connectStep === "seam_waiting" && (
            <div className="space-y-4 mt-2 text-center">
              <Loader2 className="h-10 w-10 animate-spin text-brand mx-auto" />
              <div>
                <p className="text-sm font-medium text-gray-900" data-testid="text-seam-connect-waiting">
                  Waiting for sign-in to complete…
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Finish signing in{connectingSeamProvider ? ` with ${connectingSeamProvider.charAt(0).toUpperCase() + connectingSeamProvider.slice(1)}` : ""} in the popup. This dialog will close automatically once your locks are added.
                </p>
              </div>
              {seamConnectError && (
                <p className="text-sm text-red-600">{seamConnectError}</p>
              )}
              <Button
                variant="outline"
                onClick={() => {
                  resetSeamConnectFlow();
                  setConnectStep("seam_picker");
                }}
                data-testid="button-seam-waiting-cancel"
              >
                Cancel
              </Button>
            </div>
          )}
      </ConnectLockDialog>

      <TtlockActivityDrawer
        lock={activityLock}
        onClose={() => setActivityLock(null)}
      />
    </AppLayout>
  );
}
